import React, { useEffect, useMemo, useRef, useState } from "react";
import { BootstrapProgressBar } from "./BootstrapProgressBar";
import { BootstrapGateScreen } from "./BootstrapGateScreen";
import { getPreferredRecordLabel } from "../lib/recordNumberSearch";
import { renderInlineIcon } from "../lib/icons";
import { buildGroupedNotifications, buildNotificationModuleOptions, filterNotificationGroups, getNotificationModuleMeta } from "../lib/notificationsCenter";
import { fetchFarmClimateSummary } from "../lib/climateApi";
import { getFarmClimateProfile, normalizeClimateStations } from "../lib/climateConfig";
import { buildClimateMaxVisualMeta, buildClimateVisualMeta } from "../lib/climateVisuals";
import { fetchNotificationReadMap, getCachedNotificationReadMap, mergeNotificationReadMaps, persistNotificationReadBatch, setCachedNotificationReadMap } from "../lib/notificationReadApi";
import { buildNotificationsSyncSignature, normalizeNotificationSyncSettings } from "../lib/notificationsSync";
import { registerRemoteLiveActivityDevice } from "../lib/liveActivityRemoteTokens";
import { fetchLiveOrderHydration, fetchLiveServiceHydration, fetchLiveOccurrenceHydration, reportLiveTouchDiagnosticToServer } from "../lib/liveActivities";
import { DEVICE_REGISTRY_URL, isNativeShell } from "../config/appConfig";
import { DB } from "../lib/deviceStorage";
import { shadowKey } from "../lib/syncUtils";
import { getLiveCollectionCache, setLiveCollectionCache } from "../sync/cacheApi";
import { collectionForPriorityModule } from "../lib/syncPriority";
import { getNotificationMailIcon } from "../lib/platformAssets";
import { checkNativeDeviceApproval, registerUniversalDeviceSession } from "../lib/deviceRegistry";
import { getNativeGateMessage, isNativeDeviceDataGateLocked, purgeNativeRevokedLocalData } from "../lib/nativeDeviceGate";
import { traceAndFetch } from "../sync/apiTrace"; // v3.2.08

function liveOrderIdentity(record = {}) {
  return String(record?.clientId || record?.id || record?.recordId || record?.orderId || "").trim();
}

function sameLiveOrderIdentity(a = {}, b = {}) {
  const keysA = [a?.clientId, a?.id, a?.recordId, a?.orderId].map((v) => String(v || "").trim()).filter(Boolean);
  const keysB = new Set([b?.clientId, b?.id, b?.recordId, b?.orderId].map((v) => String(v || "").trim()).filter(Boolean));
  return keysA.some((key) => keysB.has(key));
}

function liveOrderRecordNumberBase(record = {}) {
  const raw = String(
    record?.recordNumber
    || record?.record_number
    || record?.provisionalRecordNumber
    || record?.provisional_record_number
    || ""
  ).trim();
  return raw ? raw.toUpperCase().replace(/-PRV$/, "") : "";
}

function findLocalPrvForHydratedLiveOrder(hydrated = {}, sources = [], farmId = "") {
  const targetNumber = liveOrderRecordNumberBase(hydrated);
  if (!targetNumber) return null;
  const fid = String(farmId || hydrated?.farmId || "").trim();
  for (const source of sources) {
    const list = Array.isArray(source) ? source : [];
    for (const item of list) {
      if (!item || typeof item !== "object" || !isLiveOrderPrv(item)) continue;
      const itemFarmId = String(item?.farmId || "").trim();
      if (fid && itemFarmId && itemFarmId !== fid) continue;
      if (liveOrderRecordNumberBase(item) === targetNumber) return item;
    }
  }
  return null;
}

function bridgeHydratedLiveOrderToLocalPrv(hydrated = {}, sources = [], farmId = "") {
  const localPrv = findLocalPrvForHydratedLiveOrder(hydrated, sources, farmId);
  if (!localPrv || sameLiveOrderIdentity(localPrv, hydrated)) return hydrated;
  const localClientId = String(localPrv?.clientId || localPrv?.id || "").trim();
  if (!localClientId) return hydrated;
  return {
    ...hydrated,
    clientId: localClientId,
    orderId: hydrated?.orderId || hydrated?.recordId || hydrated?.id || localPrv?.orderId || localPrv?.id,
  };
}

function isLiveOrderPrv(record = {}) {
  return !!(
    record?.pendingLocalCreate
    || record?.recordNumberStatus === "provisional"
    || String(record?.recordNumber || "").toUpperCase().endsWith("-PRV")
  );
}

function isLiveOrderUdt(record = {}) {
  return !!(
    record?.pendingLocalUpdate
    || record?.updatePending
    || (record?.pendingLocalChange && (record?.pendingOperationId || record?.localOperationId))
  );
}

function liveOrderOperationId(record = {}) {
  return String(
    record?.pendingOperationId
    || record?.lastLocalOperationId
    || record?.localOperationId
    || record?.operationId
    || ""
  ).trim();
}

function liveHydrateConfirmsSameUdt(localRecord = {}, serverRecord = {}) {
  const localOp = liveOrderOperationId(localRecord);
  if (!localOp) return false;
  const serverOps = [
    serverRecord?.lastLocalOperationId,
    serverRecord?.localOperationId,
    serverRecord?.pendingOperationId,
    serverRecord?.operationId,
  ].map((v) => String(v || "").trim()).filter(Boolean);
  return !!(
    serverRecord?.serverConfirmed
    && serverRecord?.pendingLocalUpdate === false
    && serverRecord?.updatePending !== true
    && serverOps.includes(localOp)
  );
}

function liveOrderRevision(record = {}) {
  return Math.max(
    Number(record?.serverRevision || 0),
    Number(record?.baseServerRevision || 0),
    Number(record?.rowVersion || 0)
  );
}

function liveOrderTimestamp(record = {}) {
  const raw = String(record?.serverAckAt || record?.record_updated_at || record?.updatedAt || record?.localUpdatedAt || record?.createdAt || "").trim();
  const ts = raw ? Date.parse(raw) : 0;
  return Number.isFinite(ts) ? ts : 0;
}

function isServerLiveOrderNewer(localRecord = {}, serverRecord = {}) {
  const serverRev = liveOrderRevision(serverRecord);
  const localRev = liveOrderRevision(localRecord);
  if (serverRev && localRev && serverRev !== localRev) return serverRev > localRev;
  if (serverRev && !localRev) return true;
  const serverTs = liveOrderTimestamp(serverRecord);
  const localTs = liveOrderTimestamp(localRecord);
  if (serverTs && localTs && serverTs !== localTs) return serverTs > localTs;
  return false;
}

function mergeLiveOrderConservatively(existing = {}, hydrated = {}) {
  if (!existing || typeof existing !== "object") return hydrated;
  // v2.221.38: a Live vem do servidor após confirmar o pedido.
  // Ela pode resolver PRV do mesmo pedido, mas não pode apagar UDT de outra operação.
  if (isLiveOrderPrv(existing)) {
    return {
      ...existing,
      ...hydrated,
      pendingLocalCreate: false,
      pendingLocalChange: false,
      pendingLocalUpdate: false,
      updatePending: false,
      syncStatus: "synced",
      serverConfirmed: true,
    };
  }
  if (isLiveOrderUdt(existing)) {
    if (liveHydrateConfirmsSameUdt(existing, hydrated)) {
      return {
        ...existing,
        ...hydrated,
        pendingLocalUpdate: false,
        updatePending: false,
        pendingLocalChange: false,
        syncStatus: "synced",
        serverConfirmed: true,
      };
    }
    return existing;
  }
  if (isServerLiveOrderNewer(existing, hydrated)) {
    return { ...existing, ...hydrated };
  }
  // Snapshot igual/antigo: só preencher lacunas, sem regredir campos locais confirmados.
  const filled = { ...existing };
  Object.entries(hydrated || {}).forEach(([key, value]) => {
    if (filled[key] == null || filled[key] === "" || (Array.isArray(filled[key]) && filled[key].length === 0)) {
      filled[key] = value;
    }
  });
  return filled;
}

function normalizeLiveHydratedOrder(record = {}, recordId = "") {
  const now = new Date().toISOString();
  const rawClientId = String(record?.clientId || record?.client_id || record?.clientID || "").trim();
  const rawRecordId = String(record?.recordId || record?.record_id || record?.orderId || record?.order_id || recordId || "").trim();
  const id = String(record?.id || rawClientId || rawRecordId || recordId || "").trim();
  return {
    ...(record || {}),
    id,
    clientId: String(rawClientId || record?.id || id || recordId || "").trim(),
    recordId: String(rawRecordId || id || recordId || "").trim(),
    serverConfirmed: true,
    pendingLocalCreate: false,
    pendingLocalUpdate: false,
    updatePending: false,
    pendingLocalChange: false,
    syncStatus: "synced",
    liveHydrated: true,
    liveHydratedAt: now,
  };
}

async function injectLiveHydratedOrderLocally({ farmId, record, recordId }) {
  const fid = String(farmId || "").trim();
  const rid = String(recordId || record?.id || record?.clientId || record?.recordId || "").trim();
  if (!fid || !rid || !record || typeof record !== "object") return { injected: false, reason: "missing_args" };
  const localKey = `fz:${fid}:orders`;
  const shadowK = shadowKey(fid, "orders");
  let hydrated = normalizeLiveHydratedOrder(record, rid);
  const liveLocal = getLiveCollectionCache(localKey);
  const storedLocal = liveLocal !== undefined ? liveLocal : ((await DB.get(localKey)) || []);
  const liveShadow = getLiveCollectionCache(shadowK);
  const storedShadow = liveShadow !== undefined ? liveShadow : ((await DB.get(shadowK)) || []);
  // v2.221.39: quando a Live confirma um pedido que ainda existe localmente como PRV,
  // o endpoint pode trazer o id confirmado sem o clientId local. Antes do upsert,
  // fazemos a ponte pelo recordNumber base para resolver o PRV em vez de inserir duplicado.
  hydrated = bridgeHydratedLiveOrderToLocalPrv(hydrated, [storedLocal, storedShadow], fid);
  const mergeList = (current) => {
    const list = Array.isArray(current) ? current : [];
    const idx = list.findIndex((item) => sameLiveOrderIdentity(item, hydrated));
    if (idx >= 0) {
      const existing = list[idx];
      const merged = mergeLiveOrderConservatively(existing, hydrated);
      if (merged === existing || JSON.stringify(merged) === JSON.stringify(existing)) {
        return { list, changed: false, reason: isLiveOrderUdt(existing) ? "local_udt_preserved" : "kept_existing" };
      }
      const next = list.slice();
      next[idx] = { ...merged, liveHydrated: true, liveHydratedAt: hydrated.liveHydratedAt };
      return { list: next, changed: true, reason: isLiveOrderPrv(existing) ? "resolved_prv" : "merged_existing" };
    }
    return { list: [...list, hydrated], changed: true, reason: "inserted" };
  };
  const localMerge = mergeList(storedLocal);
  const shadowMerge = mergeList(storedShadow);
  if (localMerge.changed) {
    await DB.set(localKey, localMerge.list);
    setLiveCollectionCache(localKey, localMerge.list);
  }
  if (shadowMerge.changed) {
    await DB.set(shadowK, shadowMerge.list);
    setLiveCollectionCache(shadowK, shadowMerge.list);
  }
  const eventOrder = (() => {
    const localList = Array.isArray(localMerge.list) ? localMerge.list : [];
    return localList.find((item) => sameLiveOrderIdentity(item, hydrated)) || hydrated;
  })();
  try { window.__fzLastLiveHydratedOrder = { farmId: fid, recordId: rid, order: eventOrder, at: new Date().toISOString() }; } catch (_) {}
  if (localMerge.changed || shadowMerge.changed) {
    try { window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: localKey, source: "live-order-hydrate" } })); } catch (_) {}
    try { window.dispatchEvent(new CustomEvent("fz:live-order-hydrated", { detail: { farmId: fid, recordId: rid, order: eventOrder } })); } catch (_) {}
  }
  return {
    injected: localMerge.changed || shadowMerge.changed,
    localReason: localMerge.reason,
    shadowReason: shadowMerge.reason,
    recordId: rid,
    id: liveOrderIdentity(hydrated),
  };
}


// v3.5.01: equivalente de injectLiveHydratedOrderLocally para services.
// Sem lógica de PRV bridging (services não têm provisional number).
async function injectLiveHydratedServiceLocally({ farmId, record, recordId }) {
  const fid = String(farmId || "").trim();
  const rid = String(recordId || record?.id || record?.clientId || record?.recordId || "").trim();
  if (!fid || !rid || !record || typeof record !== "object") return { injected: false, reason: "missing_args" };
  const localKey = `fz:${fid}:services`;
  const shadowK  = shadowKey(fid, "services");
  const hydrated = {
    ...(record || {}),
    id:              String(record?.id       || record?.clientId || rid || "").trim(),
    clientId:        String(record?.clientId || record?.id       || rid || "").trim(),
    recordId:        String(record?.recordId || record?.id       || rid || "").trim(),
    serverConfirmed: true,
    pendingLocalCreate: false,
    pendingLocalUpdate: false,
    updatePending: false,
    pendingLocalChange: false,
    syncStatus: "synced",
    liveHydrated: true,
    liveHydratedAt: new Date().toISOString(),
  };
  const liveLocal  = getLiveCollectionCache(localKey);
  const storedLocal  = liveLocal  !== undefined ? liveLocal  : ((await DB.get(localKey))  || []);
  const liveShadow = getLiveCollectionCache(shadowK);
  const storedShadow = liveShadow !== undefined ? liveShadow : ((await DB.get(shadowK)) || []);

  const sameId = (a, b) => a && b && (
    String(a?.id || a?.clientId || "") === String(b?.id || b?.clientId || "") ||
    String(a?.recordId || "") === String(b?.recordId || "")
  );

  const mergeList = (current) => {
    const list = Array.isArray(current) ? current : [];
    const idx = list.findIndex(item => sameId(item, hydrated));
    if (idx >= 0) {
      const existing = list[idx];
      // Preservar pendentes locais: se há pendingLocalUpdate, manter local.
      if (existing?.pendingLocalUpdate || existing?.updatePending) {
        return { list, changed: false, reason: "local_udt_preserved" };
      }
      const merged = { ...existing, ...hydrated };
      if (JSON.stringify(merged) === JSON.stringify(existing)) {
        return { list, changed: false, reason: "kept_existing" };
      }
      const next = list.slice();
      next[idx] = merged;
      return { list: next, changed: true, reason: "merged_existing" };
    }
    return { list: [...list, hydrated], changed: true, reason: "inserted" };
  };

  const localMerge  = mergeList(storedLocal);
  const shadowMerge = mergeList(storedShadow);
  if (localMerge.changed) {
    await DB.set(localKey, localMerge.list);
    setLiveCollectionCache(localKey, localMerge.list);
  }
  if (shadowMerge.changed) {
    await DB.set(shadowK, shadowMerge.list);
    setLiveCollectionCache(shadowK, shadowMerge.list);
  }
  const eventRecord = (() => {
    const localList = Array.isArray(localMerge.list) ? localMerge.list : [];
    return localList.find(item => sameId(item, hydrated)) || hydrated;
  })();
  try { window.__fzLastLiveHydratedService = { farmId: fid, recordId: rid, record: eventRecord, at: new Date().toISOString() }; } catch (_) {}
  if (localMerge.changed || shadowMerge.changed) {
    const serviceHydrateDetail = {
      key: localKey,
      source: "live-service-hydrate",
      collection: "services",
      farmId: fid,
      recordId: rid,
      record: eventRecord,
      preferLive: true,
    };
    try { window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: serviceHydrateDetail })); } catch (_) {}
    // v3.5.07: além do evento específico do hybrid guard, disparar também os
    // eventos de hidratação de coleção que useFarmCollection escuta. Sem isso,
    // o toque na Live podia injetar no IDB/cache, mas a lista de Serviços só
    // refletia o registro no próximo pull_delta periódico.
    try { window.dispatchEvent(new CustomEvent("fz:collection:hydrated", { detail: serviceHydrateDetail })); } catch (_) {}
    try { window.dispatchEvent(new CustomEvent("fz:services:hydrated", { detail: serviceHydrateDetail })); } catch (_) {}
    try {
      window.dispatchEvent(new CustomEvent("fz:live-service-hydrated", {
        detail: serviceHydrateDetail,
      }));
    } catch (_) {}
  }
  return {
    injected: localMerge.changed || shadowMerge.changed,
    localReason: localMerge.reason,
    shadowReason: shadowMerge.reason,
    recordId: rid,
  };
}

// v3.6.00: equivalente de injectLiveHydratedServiceLocally para occurrences.
async function injectLiveHydratedOccurrenceLocally({ farmId, record, recordId }) {
  const fid = String(farmId || "").trim();
  const rid = String(recordId || record?.id || record?.clientId || record?.recordId || "").trim();
  if (!fid || !rid || !record || typeof record !== "object") return { injected: false, reason: "missing_args" };
  const localKey = `fz:${fid}:occurrences`;
  const shadowK  = shadowKey(fid, "occurrences");
  const hydrated = {
    ...(record || {}),
    id:              String(record?.id       || record?.clientId || rid || "").trim(),
    clientId:        String(record?.clientId || record?.id       || rid || "").trim(),
    recordId:        String(record?.recordId || record?.id       || rid || "").trim(),
    serverConfirmed: true,
    pendingLocalCreate: false,
    pendingLocalUpdate: false,
    updatePending: false,
    pendingLocalChange: false,
    syncStatus: "synced",
    liveHydrated: true,
    liveHydratedAt: new Date().toISOString(),
  };
  const liveLocal  = getLiveCollectionCache(localKey);
  const storedLocal  = liveLocal  !== undefined ? liveLocal  : ((await DB.get(localKey))  || []);
  const liveShadow = getLiveCollectionCache(shadowK);
  const storedShadow = liveShadow !== undefined ? liveShadow : ((await DB.get(shadowK)) || []);

  const sameId = (a, b) => a && b && (
    String(a?.id || a?.clientId || "") === String(b?.id || b?.clientId || "") ||
    String(a?.recordId || "") === String(b?.recordId || "")
  );

  const mergeList = (current) => {
    const list = Array.isArray(current) ? current : [];
    const idx = list.findIndex(item => sameId(item, hydrated));
    if (idx >= 0) {
      const existing = list[idx];
      if (existing?.pendingLocalUpdate || existing?.updatePending) {
        return { list, changed: false, reason: "local_udt_preserved" };
      }
      const merged = { ...existing, ...hydrated };
      if (JSON.stringify(merged) === JSON.stringify(existing)) {
        return { list, changed: false, reason: "kept_existing" };
      }
      const next = list.slice();
      next[idx] = merged;
      return { list: next, changed: true, reason: "merged_existing" };
    }
    return { list: [...list, hydrated], changed: true, reason: "inserted" };
  };

  const localMerge  = mergeList(storedLocal);
  const shadowMerge = mergeList(storedShadow);
  if (localMerge.changed) {
    await DB.set(localKey, localMerge.list);
    setLiveCollectionCache(localKey, localMerge.list);
  }
  if (shadowMerge.changed) {
    await DB.set(shadowK, shadowMerge.list);
    setLiveCollectionCache(shadowK, shadowMerge.list);
  }
  const eventRecord = (() => {
    const localList = Array.isArray(localMerge.list) ? localMerge.list : [];
    return localList.find(item => sameId(item, hydrated)) || hydrated;
  })();
  try { window.__fzLastLiveHydratedOccurrence = { farmId: fid, recordId: rid, record: eventRecord, at: new Date().toISOString() }; } catch (_) {}
  if (localMerge.changed || shadowMerge.changed) {
    const occurrenceHydrateDetail = {
      key: localKey,
      source: "live-occurrence-hydrate",
      collection: "occurrences",
      farmId: fid,
      recordId: rid,
      record: eventRecord,
      preferLive: true,
    };
    try { window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: occurrenceHydrateDetail })); } catch (_) {}
    try { window.dispatchEvent(new CustomEvent("fz:collection:hydrated", { detail: occurrenceHydrateDetail })); } catch (_) {}
    try { window.dispatchEvent(new CustomEvent("fz:occurrences:hydrated", { detail: occurrenceHydrateDetail })); } catch (_) {}
    try { window.dispatchEvent(new CustomEvent("fz:live-occurrence-hydrated", { detail: occurrenceHydrateDetail })); } catch (_) {}
  }
  return {
    injected: localMerge.changed || shadowMerge.changed,
    localReason: localMerge.reason,
    shadowReason: shadowMerge.reason,
    recordId: rid,
  };
}


function buildDashboardClimateCards(summary = null) {
  const latestSnapshot = summary?.latest_snapshot || null;
  const sourceLabel = String(summary?.source_station_name || summary?.source_station_id || "estação principal").trim() || "estação principal";
  if (!latestSnapshot) {
    return [
      { id:"current", title:"Temperatura Atual", accent:"🌡️", visual:null, value:"ND", helper:"Ainda não há leitura consolidada da estação principal.", detail:`Quando a estação ${sourceLabel} tiver leitura consolidada, este card passa a mostrar a temperatura atual do dia.` },
      { id:"max", title:"Máxima do Dia", accent:"☀️", visual:null, value:"ND", helper:"Aguardando leituras do dia.", detail:`A máxima diária será calculada a partir das leituras brutas da estação ${sourceLabel}.` },
      { id:"dynamic", title:"Chuva / Umidade", accent:"💧", visual:null, value:"ND", helper:"Sem chuva ou pico de umidade consolidado ainda.", detail:`Este card alterna entre chuva do dia e umidade no pico conforme a leitura consolidada da estação ${sourceLabel}.` },
    ];
  }
  const observedOn = String(latestSnapshot?.observed_on || summary?.latest_observed_on || "").trim();
  const currentTemp = latestSnapshot?.temp_current_c == null ? "—" : `${Number(latestSnapshot.temp_current_c).toFixed(1)} °C`;
  const maxTemp = latestSnapshot?.temp_max_c == null ? "—" : `${Number(latestSnapshot.temp_max_c).toFixed(1)} °C`;
  const humidityCurrent = latestSnapshot?.humidity_current_pct == null ? null : `${Number(latestSnapshot.humidity_current_pct).toFixed(0)}%`;
  const observedLabel = observedOn ? observedOn.split('T')[0] : "";
  const currentVisual = buildClimateVisualMeta(summary);
  const maxVisual = buildClimateMaxVisualMeta(summary);
  return [
    { id:"current", title:"Temperatura Atual", accent:"🌡️", visual: currentVisual, value:currentTemp, helper: observedLabel ? `Última leitura em ${observedLabel}.` : "Leitura atual indisponível.", detail:`Temperatura atual da última leitura bruta válida da estação ${sourceLabel}${observedLabel ? ` em ${observedLabel}` : ""}.` },
    { id:"max", title:"Máxima do Dia", accent:"☀️", visual: maxVisual, value:maxTemp, helper:"Máxima registrada no dia da última leitura.", detail:`Máxima diária consolidada da estação ${sourceLabel}.` },
    { id:"dynamic", title:"Umidade Atual", accent:"💧", visual:null, value:humidityCurrent || "—", helper: observedLabel ? `Última leitura em ${observedLabel}.` : "Leitura atual indisponível.", detail:`Umidade atual da última leitura bruta válida da estação ${sourceLabel}.` },
  ];
}


function renderDashboardMetricValue(value, color, options = {}) {
  const { compact = false } = options;
  const text = String(value ?? "—").trim();
  const numberFontSize = compact ? 22 : 24;
  const unitFontSize = compact ? 13 : 14;
  const regularFontSize = compact ? 22 : 24;
  const match = text.match(/^(-?\d+(?:[.,]\d+)?)\s*(°C)$/i);
  if (!match) return <span style={{ fontSize:regularFontSize,fontWeight:800,color,lineHeight:1,whiteSpace:"nowrap" }}>{text}</span>;
  return (
    <span style={{ display:"inline-flex", alignItems:"baseline", gap:2, flexWrap:"nowrap", whiteSpace:"nowrap", color, minWidth:0 }}>
      <span style={{ fontSize:numberFontSize,fontWeight:800,lineHeight:1 }}>{match[1]}</span>
      <span style={{ fontSize:unitFontSize,fontWeight:800,lineHeight:1 }}>{match[2]}</span>
    </span>
  );
}

function parseColorToRgb(color) {
  const raw = String(color || "").trim();
  if (!raw) return null;
  const hex = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const value = hex[1].length === 3
      ? hex[1].split("").map((part)=> part + part).join("")
      : hex[1];
    return {
      r: parseInt(value.slice(0, 2), 16),
      g: parseInt(value.slice(2, 4), 16),
      b: parseInt(value.slice(4, 6), 16),
    };
  }
  const rgb = raw.match(/^rgba?\(([^)]+)\)$/i);
  if (!rgb) return null;
  const parts = rgb[1].split(",").map((part)=> Number(String(part).trim()));
  if (parts.length < 3 || parts.slice(0, 3).some((value)=> Number.isNaN(value))) return null;
  return { r: parts[0], g: parts[1], b: parts[2] };
}

function isRedLikeColor(color) {
  const rgb = parseColorToRgb(color);
  if (!rgb) return false;
  return rgb.r >= 120 && rgb.r > rgb.g * 1.3 && rgb.r > rgb.b * 1.18;
}

function resolveShortcutBarTint(color, fallbackColor) {
  const base = String(color || fallbackColor || "#3B82F6").trim() || String(fallbackColor || "#3B82F6").trim();
  return isRedLikeColor(base) ? "#991B1B" : base;
}

// ── Unified Service Model ─────────────────────────────────────────────────────
// type: "internal" | "occurrence" | "external"
// All three use the same collection "services"
// Occurrences stay in "occurrences" collection but are viewed in services module too

export default function FarmDashboard({
  farm,
  session,
  isGestor,
  onBack,
  onLogout,
  onSessionUpdate,
  themeRevision,
  deps = {},
}) {
  const {
    useDisplayedAppVersion,
    useSync,
    VISIBLE_MODULES,
    canViewModule,
    normalizeUiPreferences,
    makeSeed,
    useFarmCollection,
    useGlobalCollection,
    normalizeAdminGlobalsConfig,
    getDeviceId,
    createClientId,
    NOTIFICATIONS_DEVICE_URL,
    API_SECRET,
    MAPS_PLATFORM,
    normalizeServiceType,
    itemMatchesCostCenterAccess,
    canViewCostCenter,
    warmUsageBaseModuleKey,
    MODULE_PRIORITY_WARM_PLAN,
    trackContextualWarmUsage,
    recordSessionWarmNavigation,
    getSessionWarmCandidates,
    runControlledPriorityWarm,
    getWarmControlDeps,
    trackWarmUsage,
    SHORTCUT_MODULE_CHOICES,
    getContrastTextColor,
    syncFarmCollaboratorAccountsToEmployees,
    MODULES,
    ModuleHeaderMenu,
    OccurrencesModule,
    ServicesModule,
    PurchasesHubModule,
    AnimalsModule,
    AvicultureModule,
    ClimateModule,
    FinancialModule,
    HRModule,
    EquipamentosEPIsModule,
    VetModule,
    StockModule,
    MachineryModule,
    HarasModule,
    LocationHubModule,
    SedeModule,
    InspectionsModule,
    MeuPerfilModule,
    ShortcutMonoIcon,
    getShortcutChoiceMeta,
    FarmEmoji,
    SyncStatusBar,
    Modal,
    Badge,
    Btn,
    fmtDate,
    LIGHT_THEME,
    T,
  } = deps;
  const displayVersion = useDisplayedAppVersion();
  const nativeFixedTop = isNativeShell();
  const nativeHeaderSpacer = "calc(env(safe-area-inset-top) + 56px)";
  const syncCtx = useSync();
  const bootstrapState = syncCtx?.bootstrapState || null;
  const retryBootstrap = syncCtx?.retryBootstrapPostDelta;
  const farmId = farm?.id || "";
  const allowedMods = ((!isGestor && session?.role==="solicitante")
    ? VISIBLE_MODULES.filter(m=>canViewModule(session,m.id))
    : VISIBLE_MODULES).filter(m=>m.id!=="providers");
  const dashboardCanOpenClimate = allowedMods.some(m=>m.id === "climate");
  const[module_,setModule]=useState(null);
  const[moduleSessionOverride,setModuleSessionOverride]=useState(null);
  const currentUiPreferences = useMemo(() => normalizeUiPreferences(session?.uiPreferences), [session?.uiPreferences, normalizeUiPreferences]);
  const[moduleBridgeMeta,setModuleBridgeMeta]=useState(null);
  const[farmNavOpen,setFarmNavOpen]=useState(false);
  const[moduleHeaderMenuMeta,setModuleHeaderMenuMeta]=useState(null);
  const[moduleHeaderMenuCache,setModuleHeaderMenuCache]=useState({});
  const[moduleRenderVersion,setModuleRenderVersion]=useState(0);
  const[purchaseOpenOrder,setPurchaseOpenOrder]=useState(null); // order id to open in purchases
  const[serviceOpenRecord,setServiceOpenRecord]=useState(null);
  // v2.228.07: referência estável para evitar loop de requestPrioritySync no ServicesModule
  const handleClearServiceOpenRecord = React.useCallback(() => {
    setServiceOpenRecord(null);
  }, []);
  const[occurrenceOpenRecord,setOccurrenceOpenRecord]=useState(null);
  // v2.229.00: referência estável para abertura única de Ocorrências via Live/deep link.
  const handleClearOccurrenceOpenRecord = React.useCallback(() => {
    setOccurrenceOpenRecord(null);
  }, []);
  // v3.8.04: UX explícita ao abrir registro pela Live Activity.
  // O app abre o módulo imediatamente com os dados disponíveis e mostra que está
  // buscando a versão confirmada do servidor, em vez de parecer travado/vazio.
  const[liveOpenHydration,setLiveOpenHydration]=useState(null);
  const beginLiveOpenHydration = React.useCallback((payload = {}) => {
    const moduleId = String(payload?.module || payload?.collection || "purchases").trim().toLowerCase();
    const recordId = String(payload?.orderId || payload?.recordId || payload?.id || "").trim();
    if (!recordId) return;
    setLiveOpenHydration({
      id: `${Date.now()}:${moduleId}:${recordId}`,
      module: moduleId === "orders" ? "purchases" : moduleId,
      collection: moduleId === "purchases" ? "orders" : moduleId,
      recordId,
      farmId: String(payload?.farmId || farm?.id || "").trim(),
      status: "loading",
      progress: 12,
      startedAt: Date.now(),
      updatedAt: Date.now(),
      payload: { ...(payload || {}), recordId, orderId: String(payload?.orderId || recordId || "") },
      message: "Recebendo dados do servidor...",
    });
  }, [farm?.id]);
  const updateLiveOpenHydration = React.useCallback((recordId, patch = {}) => {
    const rid = String(recordId || "").trim();
    if (!rid) return;
    setLiveOpenHydration((current) => {
      if (!current || String(current.recordId || "") !== rid) return current;
      return { ...current, ...patch, updatedAt: Date.now() };
    });
  }, []);
  const clearLiveOpenHydrationSoon = React.useCallback((recordId, delayMs = 1400) => {
    const rid = String(recordId || "").trim();
    window.setTimeout(() => {
      setLiveOpenHydration((current) => {
        if (!current || String(current.recordId || "") !== rid) return current;
        if (current.status !== "done") return current;
        return null;
      });
    }, delayMs);
  }, []);
  const retryLiveOpenHydration = React.useCallback(() => {
    const payload = liveOpenHydration?.payload || null;
    if (!payload) return;
    beginLiveOpenHydration({ ...payload, manualRetry: true, retryAt: new Date().toISOString() });
    try { window.dispatchEvent(new CustomEvent("fz:live-activity-open-record", { detail: { ...payload, manualRetry: true } })); } catch (_) {}
  }, [beginLiveOpenHydration, liveOpenHydration]);
  const[inspectionOpenRecord,setInspectionOpenRecord]=useState(null);
  const[poiPreset,setPoiPreset]=useState(null);
  const[servicesTabPreset,setServicesTabPreset]=useState(null);
  const seed=makeSeed();
  const[orders]      =useFarmCollection("orders",seed.orders);
  const[stock]       =useGlobalCollection("stock",seed.stock);
  const [adminGlobalsConfigRows] = useGlobalCollection("adminGlobalsConfig", [normalizeAdminGlobalsConfig(null)]);
  const adminGlobalsConfig = useMemo(()=>normalizeAdminGlobalsConfig((adminGlobalsConfigRows||[])[0]), [adminGlobalsConfigRows, normalizeAdminGlobalsConfig]);
  const climateProfiles = useMemo(()=>adminGlobalsConfig?.farmClimateProfiles || {}, [adminGlobalsConfig]);
  const climateGlobalProfile = useMemo(()=>adminGlobalsConfig?.climateGlobalProfile || {}, [adminGlobalsConfig]);
  const[notifications]=useFarmCollection("notifications",[]);
  const[employees]   =useFarmCollection("employees", []);
  const[services]    =useFarmCollection("services",[]);
  const[occurrences] =useFarmCollection("occurrences",[]);
  const[inspections] =useFarmCollection("inspections",[]);
  const[vetRecords] =useFarmCollection("vet", []);
  const[stockMovements] = useFarmCollection("stockMovements", []);
  const notificationDeviceId = useMemo(()=> getDeviceId(), [getDeviceId]);
  const [nativeDeviceVerification, setNativeDeviceVerification] = useState(null);
  const [nativeApprovalChecking, setNativeApprovalChecking] = useState(false);
  const [nativeApprovalMessage, setNativeApprovalMessage] = useState("");
  const [nativeApprovalCooldownUntil, setNativeApprovalCooldownUntil] = useState(0);
  const [nativeApprovalNow, setNativeApprovalNow] = useState(() => Date.now());
  // v2.224.07: status explícito do device, fonte de verdade do gate.
  // Conservador em app nativo: bloqueia até check_status/register confirmar aprovação.
  const [nativeDeviceStatus, setNativeDeviceStatus] = useState(() => isNativeShell() ? "pending" : "active");
  const [nativeRevokedMemoryPurged, setNativeRevokedMemoryPurged] = useState(false);
  const isNativeGateLocked = isNativeDeviceDataGateLocked(nativeDeviceStatus);
  const isNativeOperationalDataHidden = isNativeShell() && (isNativeGateLocked || nativeRevokedMemoryPurged);
  const nativeGateMessage = getNativeGateMessage(nativeDeviceStatus);

  useEffect(() => {
    if (!liveOpenHydration || liveOpenHydration.status !== "loading") return undefined;
    const startedAt = Number(liveOpenHydration.startedAt || Date.now());
    const timer = window.setInterval(() => {
      setLiveOpenHydration((current) => {
        if (!current || current.id !== liveOpenHydration.id || current.status !== "loading") return current;
        const elapsed = Date.now() - startedAt;
        if (elapsed >= 30000) {
          return {
            ...current,
            status: "timeout",
            progress: Math.max(Number(current.progress || 0), 90),
            message: "O servidor ainda não devolveu todos os dados deste registro.",
            updatedAt: Date.now(),
          };
        }
        const target = Math.min(90, 12 + Math.floor((elapsed / 30000) * 78));
        return { ...current, progress: Math.max(Number(current.progress || 0), target), updatedAt: Date.now() };
      });
    }, 700);
    return () => window.clearInterval(timer);
  }, [liveOpenHydration]);

  useEffect(() => {
    if (!liveOpenHydration) return undefined;
    const onCollectionHydrated = (event) => {
      const detail = event?.detail || {};
      const detailRecordId = String(detail?.recordId || detail?.orderId || detail?.record?.recordId || detail?.record?.id || detail?.record?.clientId || "").trim();
      const detailCollection = String(detail?.collection || "").trim();
      const expectedCollection = String(liveOpenHydration.collection || (liveOpenHydration.module === "purchases" ? "orders" : liveOpenHydration.module) || "").trim();
      if (!detailRecordId || detailRecordId !== String(liveOpenHydration.recordId || "")) return;
      if (expectedCollection && detailCollection && expectedCollection !== detailCollection) return;
      updateLiveOpenHydration(liveOpenHydration.recordId, { status: "done", progress: 100, message: "Dados recebidos do servidor." });
      clearLiveOpenHydrationSoon(liveOpenHydration.recordId);
    };
    window.addEventListener("fz:collection:hydrated", onCollectionHydrated);
    window.addEventListener("fz:sync:updated", onCollectionHydrated);
    window.addEventListener("fz:live-order-hydrated", onCollectionHydrated);
    window.addEventListener("fz:live-service-hydrated", onCollectionHydrated);
    window.addEventListener("fz:live-occurrence-hydrated", onCollectionHydrated);
    return () => {
      window.removeEventListener("fz:collection:hydrated", onCollectionHydrated);
      window.removeEventListener("fz:sync:updated", onCollectionHydrated);
      window.removeEventListener("fz:live-order-hydrated", onCollectionHydrated);
      window.removeEventListener("fz:live-service-hydrated", onCollectionHydrated);
      window.removeEventListener("fz:live-occurrence-hydrated", onCollectionHydrated);
    };
  }, [liveOpenHydration, updateLiveOpenHydration, clearLiveOpenHydrationSoon]);

  useEffect(() => {
    const onVerificationCode = (event) => {
      const detail = event?.detail || null;
      setNativeDeviceVerification(detail);
      if (detail?.code) setNativeDeviceStatus("pending");
    };
    window.addEventListener("fz:device-verification-code", onVerificationCode);
    try {
      if (window.__fzNativeDeviceVerification) {
        setNativeDeviceVerification(window.__fzNativeDeviceVerification);
        setNativeDeviceStatus("pending");
      }
    } catch (_) {}
    return () => window.removeEventListener("fz:device-verification-code", onVerificationCode);
  }, []);

  useEffect(() => {
    if (typeof syncCtx?.setNativeDeviceGateLock !== "function") return undefined;
    syncCtx.setNativeDeviceGateLock(isNativeGateLocked);
    return () => syncCtx.setNativeDeviceGateLock(false);
  }, [isNativeGateLocked, syncCtx]);


  useEffect(() => {
    if (!isNativeShell()) return undefined;
    const onNativeRevokedPurge = () => {
      setNativeRevokedMemoryPurged(true);
      setModule(null);
      setModuleSessionOverride(null);
      setModuleBridgeMeta(null);
      setPurchaseOpenOrder(null);
      setServiceOpenRecord(null);
      setOccurrenceOpenRecord(null);
      setServicesTabPreset(null);
    };
    window.addEventListener("fz:native-device-revoked-purge-start", onNativeRevokedPurge);
    window.addEventListener("fz:native-device-revoked-purge", onNativeRevokedPurge);
    return () => {
      window.removeEventListener("fz:native-device-revoked-purge-start", onNativeRevokedPurge);
      window.removeEventListener("fz:native-device-revoked-purge", onNativeRevokedPurge);
    };
  }, []);

  useEffect(() => {
    if (nativeDeviceStatus === "verified" || nativeDeviceStatus === "active") setNativeRevokedMemoryPurged(false);
  }, [nativeDeviceStatus]);


  useEffect(() => {
    if (!isNativeShell() || nativeDeviceStatus !== "revoked") return undefined;
    let cancelled = false;
    purgeNativeRevokedLocalData({ farmId: farm?.id, deviceId: getDeviceId?.() || "" }).then(() => {
      if (cancelled) return;
      try { onLogout?.({ confirm: false, reason: "native-device-revoked" }); } catch (_) {}
    }).catch(() => null);
    return () => { cancelled = true; };
  }, [farm?.id, getDeviceId, nativeDeviceStatus, onLogout]);

  const deviceRegistrySessionAccountId = session?.accountId || session?.id || "";
  const deviceRegistrySessionUserId = session?.userId || "";
  const deviceRegistrySessionUsername = session?.username || "";
  const deviceRegistrySessionName = session?.name || session?.userName || session?.username || "";
  const deviceRegistrySessionLoginId = session?.sessionId || session?.loginId || session?.id || "";
  const deviceRegistrySessionRole = session?.role || session?.userRole || ""; // v3.2.08
  const deviceRegistrySession = useMemo(() => {
    if (!deviceRegistrySessionAccountId && !deviceRegistrySessionUserId && !deviceRegistrySessionUsername && !deviceRegistrySessionName) return null;
    return {
      accountId: deviceRegistrySessionAccountId,
      userId: deviceRegistrySessionUserId,
      username: deviceRegistrySessionUsername,
      name: deviceRegistrySessionName,
      sessionId: deviceRegistrySessionLoginId,
      role: deviceRegistrySessionRole, // v3.2.08
    };
  }, [deviceRegistrySessionAccountId, deviceRegistrySessionUserId, deviceRegistrySessionUsername, deviceRegistrySessionName, deviceRegistrySessionLoginId, deviceRegistrySessionRole]);
  const deviceRegistrySessionId = deviceRegistrySessionAccountId || deviceRegistrySessionUserId || deviceRegistrySessionUsername || deviceRegistrySessionName || "";
  const deviceRegistryIsAdminRole = ["admin", "administrador", "gestor", "manager"].includes(
    String(deviceRegistrySession?.role || "").toLowerCase().trim()
  );
  const deviceRegistryEffectiveFarmId = deviceRegistryIsAdminRole ? "__admin_global__" : farm?.id;
  useEffect(() => {
    let cancelled = false;
    if (!farm?.id || !deviceRegistrySessionId || !deviceRegistrySession) return () => { cancelled = true; };
    registerUniversalDeviceSession({ farmId: farm.id, session: deviceRegistrySession, reason: "farm-dashboard" })
      .then((result) => {
        if (cancelled) return null;
        const status = String(result?.data?.data?.device_status || "").toLowerCase().trim();
        if (status) setNativeDeviceStatus(status);
        return null;
      })
      .catch(() => null);
    return () => { cancelled = true; };
  }, [farm?.id, deviceRegistrySessionId, deviceRegistrySession]);

  // v2.224.07: monitor automático de aprovação/revogação a cada 30s no app nativo.
  // Usa fetch direto ao check_status para não consumir o cooldown do botão manual.
  useEffect(() => {
    if (!isNativeShell()) return undefined;
    if (!farm?.id || !deviceRegistrySession) return undefined;
    let cancelled = false;
    let intervalHandle = null;
    // v3.2.08: Admin/Gestor usa registro global, não por fazenda.
    const _effectiveFarmId = deviceRegistryEffectiveFarmId;

    async function autoCheckApproval() {
      if (cancelled) return;
      try {
        const deviceId = getDeviceId();
        const res = await traceAndFetch(DEVICE_REGISTRY_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": API_SECRET,
            "X-Device-ID": deviceId,
            "X-User-Name": deviceRegistrySession?.name || deviceRegistrySession?.username || "unknown",
          },
          body: JSON.stringify({
            action: "check_status",
            farm_id: _effectiveFarmId,
            account_id: deviceRegistrySession?.accountId || "",
            user_id: deviceRegistrySession?.userId || "",
            username: deviceRegistrySession?.username || "",
            user_name: deviceRegistrySession?.name || "",
            device_id: deviceId,
          }),
        }, { source: "device-registry.autoCheck", action: "check_status" });
        if (cancelled) return;
        if (res.status === 429) {
          if (intervalHandle) clearInterval(intervalHandle);
          intervalHandle = setInterval(autoCheckApproval, 5 * 60 * 1000);
          return;
        }
        const data = await res.json().catch(() => null);
        if (cancelled) return;
        const status = String(data?.data?.device_status || "").toLowerCase().trim();
        if (!status) return;
        setNativeDeviceStatus((prev) => prev === status ? prev : status);
        if (status === "verified" || status === "active") {
          setNativeDeviceVerification(null);
          window.__fzNativeDeviceVerification = null;
          try { localStorage.removeItem(`fz:nativeDeviceVerification:${deviceRegistryEffectiveFarmId}:${deviceId}`); } catch (_) {}
          window.dispatchEvent(new CustomEvent("fz:device-verification-code", { detail: null }));
          return;
        }

        // v2.225.01: recupera mismatch entre o hint do servidor e o código local.
        // O código canônico nasce no device; se o admin limpou o código, ou se o purge
        // deixou o aparelho com código local diferente, forçamos um register seguro.
        if (status === "pending" || status === "stale" || status === "not_registered" || status === "unknown") {
          const serverHint = String(data?.data?.verification_code_hint || "").trim();
          const localCode = String(window.__fzNativeDeviceVerification?.code || "").trim();
          const localHint = localCode.length >= 3 ? localCode.slice(-3) : "";
          const hintMismatch = serverHint === "" || (localHint !== "" && serverHint !== "" && serverHint !== localHint);
          if (hintMismatch) {
            const verificationKey = `fz:nativeDeviceVerification:${deviceRegistryEffectiveFarmId}:${deviceId}`;
            try { localStorage.removeItem(verificationKey); } catch (_) {}
            window.__fzNativeDeviceVerification = null;
            setNativeDeviceVerification(null);
            const identityKey = deviceRegistrySession?.accountId || deviceRegistrySession?.userId || deviceRegistrySession?.username || deviceRegistrySession?.name || "user";
            const throttleKey = `fz:device-registry:last:${deviceRegistryEffectiveFarmId}:${identityKey}:${deviceId}`;
            try { localStorage.removeItem(throttleKey); } catch (_) {}
            try {
              await registerUniversalDeviceSession({ farmId: _effectiveFarmId, session: deviceRegistrySession, reason: "hint-mismatch-recovery" });
            } catch (_) {}
          }
        }
      } catch (_) {}
    }

    autoCheckApproval();
    intervalHandle = setInterval(autoCheckApproval, 30 * 1000);
    return () => {
      cancelled = true;
      if (intervalHandle) clearInterval(intervalHandle);
    };
  }, [API_SECRET, deviceRegistrySession, deviceRegistryEffectiveFarmId, farm?.id, getDeviceId]);

  useEffect(() => {
    if (!nativeApprovalCooldownUntil) return undefined;
    const tick = () => setNativeApprovalNow(Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nativeApprovalCooldownUntil]);

  const nativeApprovalCooldownMs = Math.max(0, Number(nativeApprovalCooldownUntil || 0) - Number(nativeApprovalNow || Date.now()));
  const nativeApprovalCooldownSeconds = Math.ceil(nativeApprovalCooldownMs / 1000);
  const nativeApprovalButtonDisabled = nativeApprovalChecking || nativeApprovalCooldownMs > 0;
  const nativeApprovalButtonLabel = nativeApprovalCooldownMs > 0
    ? `Verificar novamente em ${Math.floor(nativeApprovalCooldownSeconds / 60)}:${String(nativeApprovalCooldownSeconds % 60).padStart(2, "0")}`
    : nativeApprovalChecking
      ? "Verificando..."
      : "Verificar aprovação";

  async function handleCheckNativeApproval() {
    if (!farm?.id || !deviceRegistrySession || nativeApprovalButtonDisabled) return;
    setNativeApprovalChecking(true);
    setNativeApprovalMessage("");
    const cooldownUntil = Date.now() + 2 * 60 * 1000;
    setNativeApprovalCooldownUntil(cooldownUntil);
    try {
      const result = await checkNativeDeviceApproval({ farmId: farm.id, session: deviceRegistrySession });
      const info = result?.data?.data || {};
      const status = String(info.device_status || "").toLowerCase().trim();
      if (status) setNativeDeviceStatus(status);
      if (status === "pending" || status === "stale" || status === "not_registered" || status === "unknown") {
        const deviceId = getDeviceId();
        const serverHint = String(info.verification_code_hint || "").trim();
        const localCode = String(window.__fzNativeDeviceVerification?.code || "").trim();
        const localHint = localCode.length >= 3 ? localCode.slice(-3) : "";
        const hintMismatch = serverHint === "" || (localHint !== "" && serverHint !== "" && serverHint !== localHint);
        if (hintMismatch) {
          const verificationKey = `fz:nativeDeviceVerification:${deviceRegistryEffectiveFarmId}:${deviceId}`;
          try { localStorage.removeItem(verificationKey); } catch (_) {}
          window.__fzNativeDeviceVerification = null;
          setNativeDeviceVerification(null);
          const identityKey = deviceRegistrySession?.accountId || deviceRegistrySession?.userId || deviceRegistrySession?.username || deviceRegistrySession?.name || "user";
          const throttleKey = `fz:device-registry:last:${deviceRegistryEffectiveFarmId}:${identityKey}:${deviceId}`;
          try { localStorage.removeItem(throttleKey); } catch (_) {}
          try {
            await registerUniversalDeviceSession({ farmId: farm.id, session: deviceRegistrySession, reason: "hint-mismatch-recovery-manual" });
          } catch (_) {}
        }
      }
      if (result?.ok && (status === "verified" || status === "active")) {
        setNativeDeviceVerification(null);
        setNativeApprovalMessage("Dispositivo aprovado com sucesso.");
        setNativeApprovalCooldownUntil(0);
      } else if (result?.throttled && result?.retryAfterMs) {
        setNativeApprovalCooldownUntil(Date.now() + Number(result.retryAfterMs || 2 * 60 * 1000));
        setNativeApprovalMessage("Aguarde para verificar novamente.");
      } else {
        if (result?.status === 429) setNativeApprovalCooldownUntil(Date.now() + 5 * 60 * 1000);
        setNativeApprovalMessage("Até o momento este dispositivo ainda não foi aprovado pelo Administrador. Entre em contato com o Administrador ou aguarde a aprovação.");
      }
    } catch (_) {
      setNativeApprovalMessage("Não foi possível verificar a aprovação agora. Tente novamente em alguns minutos.");
    } finally {
      setNativeApprovalChecking(false);
    }
  }
  const [notificationsCenterOpen, setNotificationsCenterOpen] = useState(false);
  const notificationMailIcon = getNotificationMailIcon();
  const [dashboardClimateSummary, setDashboardClimateSummary] = useState(null);
  const [dashboardClimateLoading, setDashboardClimateLoading] = useState(false);
  const [dashboardClimateExpanded, setDashboardClimateExpanded] = useState("");
  const notificationSyncSettings = React.useMemo(()=> normalizeNotificationSyncSettings(adminGlobalsConfig?.notificationSyncSettings), [adminGlobalsConfig]);
  const farmClimateProfile = useMemo(()=>getFarmClimateProfile(climateProfiles, farmId), [climateProfiles, farmId]);
  const dashboardClimateStations = useMemo(()=>normalizeClimateStations(farmClimateProfile?.stations, farmClimateProfile), [farmClimateProfile]);
  const dashboardClimateCards = useMemo(()=>buildDashboardClimateCards(dashboardClimateSummary), [dashboardClimateSummary]);
  const getNotificationIdentity = React.useCallback((item)=>{
    const directId = String(item?.clientId || item?.id || "").trim();
    if(directId) return directId;
    const entityId = String(item?.entityId || item?.orderId || "").trim();
    const event = String(item?.event || item?.type || "notification").trim();
    const recipientKey = String(session?.username || session?.userId || session?.id || "anon").trim().toLowerCase();
    return `${event}:${entityId || 'generic'}:${recipientKey}`;
  }, [session?.id, session?.userId, session?.username]);
  const [notificationReadMap, setNotificationReadMap] = useState(()=>{
    try{
      return getCachedNotificationReadMap({ farmId, session });
    }catch(_error){
      return {};
    }
  });
  const [notificationReadsLoading, setNotificationReadsLoading] = useState(false);
  const [notificationWriteBusyMap, setNotificationWriteBusyMap] = useState({});
  const notificationReadSyncRef = React.useRef({ signature: '', loadedAt: 0 });
  const notificationWriteInFlightRef = React.useRef(new Set());
  const runNotificationsSyncRef = React.useRef(null);

  useEffect(()=>{
    const cachedReads = getCachedNotificationReadMap({ farmId, session });
    if(Object.keys(cachedReads || {}).length){
      setNotificationReadMap((prev)=> mergeNotificationReadMaps(prev, cachedReads));
    }
  }, [farmId, session]);
  const registerNotificationDevice = React.useCallback(async (permissionStatus = "default")=>{
    // v2.226.00: device nativo pendente/stale/revogado não registra token
    // operacional de notificação enquanto o gate de segurança estiver ativo.
    if(isNativeGateLocked) return;
    if(!farm?.id || !session?.id) return;
    try{
      const storedTokenKey = `fz:notif:token:${notificationDeviceId}`;
      let token = localStorage.getItem(storedTokenKey);
      if(!token){
        token = createClientId("ntdev");
        localStorage.setItem(storedTokenKey, token);
      }
      await traceAndFetch(NOTIFICATIONS_DEVICE_URL, {
        method:"POST",
        headers:{
          "Content-Type":"application/json",
          "X-API-Key": API_SECRET,
          "X-Device-ID": notificationDeviceId,
          "X-User-Name": session?.name || "",
        },
        body: JSON.stringify({
          farm_id: farm.id,
          account_id: session.id,
          user_id: session.userId || "",
          username: session.username || "",
          platform: MAPS_PLATFORM || "site",
          permission_status: permissionStatus,
          token,
        }),
      }, { source: "notifications-device.register", action: "" });
    }catch(e){}
  }, [API_SECRET, MAPS_PLATFORM, NOTIFICATIONS_DEVICE_URL, createClientId, farm?.id, session?.id, session?.name, session?.userId, session?.username, notificationDeviceId, isNativeGateLocked]);

  useEffect(()=>{
    if(isNativeGateLocked) return;
    if(!farm?.id || !session?.id) return;
    let cancelled = false;
    const timer = window.setTimeout(async ()=>{
      if(cancelled) return;
      try{
        await registerRemoteLiveActivityDevice({ farm, session, deviceId: notificationDeviceId });
      }catch(_error){}
    }, 1400);
    return ()=>{ cancelled = true; window.clearTimeout(timer); };
  }, [farm, session, notificationDeviceId, isNativeGateLocked]);


  const currentNotificationRecipient = React.useMemo(()=>({
    accountId: String(session?.id || "").trim(),
    userId: String(session?.userId || "").trim(),
    username: String(session?.username || "").trim().toLowerCase(),
    name: String(session?.name || "").trim(),
  }), [session?.id, session?.userId, session?.username, session?.name]);

  const isNotificationForCurrentUser = React.useCallback((item)=>{
    const recipients = Array.isArray(item?.recipients) ? item.recipients : [];
    return recipients.some((recipient)=>{
      const rid = String(recipient?.accountId || "").trim();
      const ruid = String(recipient?.userId || "").trim();
      const run = String(recipient?.username || "").trim().toLowerCase();
      return (rid && rid === currentNotificationRecipient.accountId) || (ruid && ruid === currentNotificationRecipient.userId) || (run && run === currentNotificationRecipient.username);
    });
  }, [currentNotificationRecipient]);

  const getNotificationReadMeta = React.useCallback((item)=>{
    const notificationId = String(item?.id || item?.clientId || getNotificationIdentity(item) || "").trim();
    const remoteEntry = notificationId ? notificationReadMap?.[notificationId] : null;
    const readStatus = String(remoteEntry?.readStatus || "").trim().toLowerCase();
    const readAt = String(remoteEntry?.readAt || "").trim();
    return {
      isRead: readStatus === "read" || !!readAt,
      readAt,
      readEntry: remoteEntry || null,
    };
  }, [getNotificationIdentity, notificationReadMap]);

  const relevantNotifications = React.useMemo(()=>{
    if(isNativeOperationalDataHidden) return [];
    const list = Array.isArray(notifications) ? notifications : [];
    return list
      .filter(item=> item && ["purchase_event","service_event","occurrence_event","inspection_event","veterinary_event"].includes(String(item.type || "")) && isNotificationForCurrentUser(item))
      .sort((a,b)=> new Date(b?.eventAt || b?.createdAt || 0).getTime() - new Date(a?.eventAt || a?.createdAt || 0).getTime());
  }, [notifications, isNotificationForCurrentUser, isNativeOperationalDataHidden]);

  const relevantNotificationIds = React.useMemo(()=> relevantNotifications.map((item)=> String(item?.id || item?.clientId || getNotificationIdentity(item) || "").trim()).filter(Boolean), [relevantNotifications, getNotificationIdentity]);
  const notificationsSyncSignature = React.useMemo(()=> buildNotificationsSyncSignature({ farmId: farm?.id, session, notificationIds: relevantNotificationIds }), [farm?.id, session, relevantNotificationIds]);

  const runNotificationsSync = React.useCallback(async ({ forceRemoteReads = false, triggerCollectionSync = false } = {})=>{
    if(isNativeGateLocked) return;
    if(!farm?.id || (!currentNotificationRecipient.accountId && !currentNotificationRecipient.userId && !currentNotificationRecipient.username)) return;
    if(triggerCollectionSync && syncCtx?.syncAfterSave){
      try{ await syncCtx.syncAfterSave(farm.id, "notifications"); }catch(_e){}
    }
    const shouldRefreshReads = forceRemoteReads || notificationReadSyncRef.current.signature !== notificationsSyncSignature || !notificationReadSyncRef.current.loadedAt;
    if(!shouldRefreshReads) return;
    setNotificationReadsLoading(true);
    try{
      const nextMap = await fetchNotificationReadMap({ farmId: farm.id, session, notificationIds: relevantNotificationIds, notifications: relevantNotifications });
      setNotificationReadMap((prev)=> mergeNotificationReadMaps(prev, nextMap || {}));
      notificationReadSyncRef.current = { signature: notificationsSyncSignature, loadedAt: Date.now() };
    }catch(_e){}
    finally{
      setNotificationReadsLoading(false);
    }
  }, [currentNotificationRecipient.accountId, currentNotificationRecipient.userId, currentNotificationRecipient.username, farm?.id, notificationsSyncSignature, relevantNotificationIds, relevantNotifications, session, syncCtx, isNativeGateLocked]);

  useEffect(()=>{
    runNotificationsSyncRef.current = runNotificationsSync;
  }, [runNotificationsSync]);

  const syncNotificationsReadState = React.useCallback(async (items, shouldRead = true)=>{
    const list = (Array.isArray(items) ? items : [items]).filter(Boolean);
    const operations = [];
    const nextBusy = {};
    list.forEach((item)=>{
      const notificationId = String(item?.id || item?.clientId || getNotificationIdentity(item) || "").trim();
      if(!notificationId) return;
      const busyKey = `${notificationId}:${shouldRead ? 'read' : 'unread'}`;
      if(notificationWriteInFlightRef.current.has(busyKey)) return;
      notificationWriteInFlightRef.current.add(busyKey);
      nextBusy[notificationId] = true;
      operations.push({ notificationId, read: shouldRead, readAt: shouldRead ? new Date().toISOString() : "" });
    });
    if(!operations.length) return;
    const optimisticPatch = operations.reduce((acc, operation)=>{
      acc[operation.notificationId] = {
        readAt: String(operation.readAt || new Date().toISOString()).trim(),
        readStatus: "read",
        accountId: currentNotificationRecipient.accountId,
        userId: currentNotificationRecipient.userId,
        username: currentNotificationRecipient.username,
        name: currentNotificationRecipient.name,
      };
      return acc;
    }, {});
    // Baixa local imediata: a leitura deve sumir do contador no mesmo toque.
    // A confirmação remota consolida o mesmo estado no banco; se estiver offline, o estado local continua como lido.
    setCachedNotificationReadMap({ farmId: farm.id, session, reads: optimisticPatch });
    setNotificationReadMap((prev)=> mergeNotificationReadMaps(prev, optimisticPatch));
    setNotificationWriteBusyMap((prev)=> ({ ...prev, ...nextBusy }));
    try{
      const response = await persistNotificationReadBatch({ farmId: farm.id, session, operations, notifications: list });
      const remotePatch = operations.reduce((acc, operation)=>{
        acc[operation.notificationId] = {
          readAt: String(response?.reads?.[operation.notificationId]?.readAt || optimisticPatch?.[operation.notificationId]?.readAt || operation.readAt || new Date().toISOString()).trim(),
          readStatus: "read",
          accountId: currentNotificationRecipient.accountId,
          userId: currentNotificationRecipient.userId,
          username: currentNotificationRecipient.username,
          name: currentNotificationRecipient.name,
        };
        return acc;
      }, {});
      setCachedNotificationReadMap({ farmId: farm.id, session, reads: remotePatch });
      setNotificationReadMap((prev)=> mergeNotificationReadMaps(prev, remotePatch));
      notificationReadSyncRef.current = { signature: notificationsSyncSignature, loadedAt: Date.now() };
    }catch(_e){}
    finally{
      operations.forEach((operation)=> notificationWriteInFlightRef.current.delete(`${operation.notificationId}:${operation.read ? 'read' : 'unread'}`));
      setNotificationWriteBusyMap((prev)=>{
        const copy = { ...prev };
        operations.forEach((operation)=>{ delete copy[operation.notificationId]; });
        return copy;
      });
    }
  }, [currentNotificationRecipient.accountId, currentNotificationRecipient.name, currentNotificationRecipient.userId, currentNotificationRecipient.username, farm?.id, getNotificationIdentity, notificationsSyncSignature, session]);

  const [notificationFilters, setNotificationFilters] = useState({ query:"", moduleKey:"all", datePreset:"all", startDate:"", endDate:"", unreadOnly:false });
  const [expandedNotificationGroups, setExpandedNotificationGroups] = useState({});
  const [expandedNotificationHistoryGroups, setExpandedNotificationHistoryGroups] = useState({});

  const groupedNotifications = React.useMemo(()=> buildGroupedNotifications(relevantNotifications, {
    isReadFn:(item)=> getNotificationReadMeta(item).isRead,
    enrichGroup:(group)=>{
      const latestItem = group.latestItem || {};
      const itemType = String(latestItem?.type || "").trim();
      const linkedRecord = itemType === "purchase_event"
        ? (orders || []).find((order)=> String(order?.id || "").trim() === String(latestItem?.orderId || latestItem?.entityId || "").trim())
        : itemType === "occurrence_event"
          ? (occurrences || []).find((occ)=> String(occ?.id || "").trim() === String(latestItem?.entityId || latestItem?.orderId || "").trim())
          : itemType === "inspection_event"
            ? (inspections || []).find((insp)=> String(insp?.id || "").trim() === String(latestItem?.entityId || latestItem?.orderId || "").trim())
            : itemType === "veterinary_event"
              ? (vetRecords || []).find((vet)=> String(vet?.id || "").trim() === String(latestItem?.entityId || latestItem?.orderId || "").trim())
              : (services || []).find((svc)=> String(svc?.id || "").trim() === String(latestItem?.entityId || latestItem?.orderId || "").trim());
      const meta = getNotificationModuleMeta(latestItem);
      const orderLabel = String(latestItem?.orderRecordNumber || latestItem?.entityRecordNumber || linkedRecord?.recordNumber || group.entityLabel || "").trim();
      return {
        ...group,
        orderKey: group.groupKey,
        orderId: String(latestItem?.orderId || latestItem?.entityId || "").trim(),
        orderLabel,
        title: latestItem?.title || meta.referenceLabel,
        costCenter: group.costCenter || linkedRecord?.costCenter || "",
        requester: group.requester || linkedRecord?.requester || linkedRecord?.reportedBy || linkedRecord?.inspector || linkedRecord?.vet || "",
        latestStatus: group.latestStatus || linkedRecord?.status || "",
        type: itemType,
      };
    },
  }), [relevantNotifications, orders, services, occurrences, inspections, vetRecords, getNotificationReadMeta]);

  const notificationModuleOptions = React.useMemo(()=> buildNotificationModuleOptions(groupedNotifications), [groupedNotifications]);
  const filteredGroupedNotifications = React.useMemo(()=> filterNotificationGroups(groupedNotifications, notificationFilters), [groupedNotifications, notificationFilters]);
  const unreadNotificationCount = React.useMemo(()=> {
    // A badge do topo representa os agrupamentos exibidos na Central, não cada evento bruto.
    // Assim evitamos mostrar 6 quando a Central tem 3 registros corretos com histórico agregado.
    return groupedNotifications.filter((group)=> Number(group?.unreadCount || 0) > 0).length;
  }, [groupedNotifications]);

  const openInternalNotification = React.useCallback((item)=>{
    if(!item) return;
    syncNotificationsReadState(item, true);
    const targetId = String(item?.orderId || item?.entityId || "").trim();
    if(item?.type === "purchase_event" && targetId){
      setModule("purchases");
      setModuleSessionOverride(null);
      setModuleBridgeMeta(null);
      setPurchaseOpenOrder(targetId);
      setServiceOpenRecord(null);
      setOccurrenceOpenRecord(null);
      setNotificationsCenterOpen(false);
      return;
    }
    if(item?.type === "service_event" && targetId){
      const isExternal = String(item?.entityType || "").trim() === "service_external";
      setModule(isExternal ? "services_external" : "services");
      setModuleSessionOverride(null);
      setModuleBridgeMeta(null);
      setPurchaseOpenOrder(null);
      setOccurrenceOpenRecord(null);
      setServicesTabPreset(isExternal ? "external" : "internal");
      setServiceOpenRecord(targetId);
      setNotificationsCenterOpen(false);
      return;
    }
    if(item?.type === "occurrence_event" && targetId){
      setModule("occurrences");
      setModuleSessionOverride(null);
      setModuleBridgeMeta(null);
      setPurchaseOpenOrder(null);
      setServiceOpenRecord(null);
      setServicesTabPreset(null);
      setOccurrenceOpenRecord(targetId);
      setNotificationsCenterOpen(false);
      return;
    }
    if(item?.type === "inspection_event" && targetId){
      try{ if(typeof window !== "undefined") localStorage.setItem("fz:open-inspection-record", JSON.stringify({ farmId: String(farmId), recordId: targetId, at: new Date().toISOString() })); }catch(_e){}
      setModule("inspections");
      setModuleSessionOverride(null);
      setModuleBridgeMeta(null);
      setPurchaseOpenOrder(null);
      setServiceOpenRecord(null);
      setOccurrenceOpenRecord(null);
      setServicesTabPreset(null);
      setNotificationsCenterOpen(false);
      return;
    }
    if(item?.type === "veterinary_event" && targetId){
      try{ if(typeof window !== "undefined") localStorage.setItem("fz:admin:open-vet-record", JSON.stringify({ farmId: String(farmId), recordId: targetId, at: new Date().toISOString() })); }catch(_e){}
      setModule("vet");
      setModuleSessionOverride(null);
      setModuleBridgeMeta(null);
      setPurchaseOpenOrder(null);
      setServiceOpenRecord(null);
      setOccurrenceOpenRecord(null);
      setServicesTabPreset(null);
      setNotificationsCenterOpen(false);
    }
  }, [farmId, syncNotificationsReadState]);

  const markAllNotificationsRead = React.useCallback(()=>{
    syncNotificationsReadState(relevantNotifications, true);
  }, [relevantNotifications, syncNotificationsReadState]);

  const updateNotificationFilter = React.useCallback((key, value)=>{
    setNotificationFilters((prev)=> ({ ...prev, [key]: value }));
  }, []);

  const isNotificationGroupExpanded = React.useCallback((group)=>{
    const groupKey = String(group?.groupKey || "").trim();
    if(!groupKey) return !!group?.unreadCount;
    if(Object.prototype.hasOwnProperty.call(expandedNotificationGroups || {}, groupKey)) return !!expandedNotificationGroups[groupKey];
    return Number(group?.unreadCount || 0) > 0;
  }, [expandedNotificationGroups]);

  const toggleNotificationGroupExpanded = React.useCallback((group)=>{
    const groupKey = String(group?.groupKey || group || "").trim();
    if(!groupKey) return;
    setExpandedNotificationGroups((prev)=> {
      const hasOwn = Object.prototype.hasOwnProperty.call(prev || {}, groupKey);
      const defaultExpanded = Number(group?.unreadCount || 0) > 0;
      const current = hasOwn ? !!prev?.[groupKey] : defaultExpanded;
      return { ...prev, [groupKey]: !current };
    });
  }, []);

  const toggleNotificationHistoryExpanded = React.useCallback((groupKey)=>{
    const normalized = String(groupKey || "").trim();
    if(!normalized) return;
    setExpandedNotificationHistoryGroups((prev)=> ({ ...prev, [normalized]: !prev?.[normalized] }));
  }, []);

  const toggleReadNotification = React.useCallback((item)=>{
    const nextShouldRead = true;
    const groupKey = String(item?.groupKey || item?.orderId || item?.entityId || item?.clientId || item?.id || "").trim();
    if(groupKey){
      setExpandedNotificationGroups((prev)=> ({ ...prev, [groupKey]: !!nextShouldRead }));
    }
    syncNotificationsReadState(item, nextShouldRead);
  }, [syncNotificationsReadState]);

  const showSystemNotification = React.useCallback(async (payload)=>{
    if(!payload || typeof window === "undefined") return false;
    const title = String(payload?.title || "Notificação").trim() || "Notificação";
    const options = {
      body: String(payload?.body || payload?.summary || "").trim(),
      data: { url: payload?.url || "/" },
      tag: payload?.tag || payload?.id || undefined,
      renotify: false,
    };
    try{
      if(navigator?.serviceWorker?.ready){
        const reg = await navigator.serviceWorker.ready;
        if(reg?.active){
          reg.active.postMessage({ type:"LOCAL_NOTIFY", payload:{ ...payload, title, ...options } });
          return true;
        }
        if(reg?.showNotification){
          await reg.showNotification(title, options);
          return true;
        }
      }
    }catch(e){}
    try{
      if(typeof Notification !== "undefined" && Notification.permission === "granted"){
        const n = new Notification(title, options);
        n.onclick = ()=>{ try{ window.focus(); }catch(e){} try{ window.location.href = options?.data?.url || "/"; }catch(e){} };
        return true;
      }
    }catch(e){}
    return false;
  }, []);

  useEffect(()=>{
    if(!["services","services_external"].includes(module_) && servicesTabPreset) setServicesTabPreset(null);
  },[module_, servicesTabPreset]);

  useEffect(()=>{
    if(!module_) return;
    const scrollTop = ()=>{
      try{ window.scrollTo({ top: 0, left: 0, behavior: "auto" }); }catch(e){ try{ window.scrollTo(0,0); }catch(_e){} }
      try{
        const root = document.scrollingElement || document.documentElement || document.body;
        if(root) root.scrollTop = 0;
      }catch(e){}
      try{
        document.querySelectorAll('.module-scroll-root, [data-module-scroll-root="true"]').forEach((el)=>{ el.scrollTop = 0; });
      }catch(e){}
    };
    scrollTop();
    const raf1 = requestAnimationFrame(scrollTop);
    const raf2 = requestAnimationFrame(scrollTop);
    const t = setTimeout(scrollTop, 80);
    return ()=>{
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(t);
    };
  }, [module_]);

  useEffect(()=>{
    if(!module_) return;
    const baseModule = warmUsageBaseModuleKey(module_);
    if(!baseModule || !MODULE_PRIORITY_WARM_PLAN[baseModule]) return;
    trackContextualWarmUsage(baseModule, { farmId: farm?.id || "", includeHome: false });
    recordSessionWarmNavigation(baseModule, { farmId: farm?.id || "" });
  }, [module_, farm?.id, MODULE_PRIORITY_WARM_PLAN, recordSessionWarmNavigation, trackContextualWarmUsage, warmUsageBaseModuleKey]);

  useEffect(()=>{
    if(!module_ || !farm?.id || (typeof navigator !== "undefined" && navigator?.onLine === false)) return;
    const baseModule = warmUsageBaseModuleKey(module_);
    if(!baseModule || !MODULE_PRIORITY_WARM_PLAN[baseModule]) return;
    const sessionCandidates = getSessionWarmCandidates({
      farmId: farm.id,
      currentModule: baseModule,
      limitPerScope: 3,
    }).filter((moduleId)=>moduleId !== baseModule).slice(0, 3);
    if(!sessionCandidates.length) return;
    const t = setTimeout(()=>{
      runControlledPriorityWarm({
        scope: "session",
        reason: `module-session:${baseModule}`,
        farmId: farm.id,
        userName: session?.name || "session-warm",
        moduleIds: sessionCandidates,
        freshnessMs: 120000,
        currentModule: baseModule,
        maxModules: 3,
      }, getWarmControlDeps()).catch(()=>{});
      sessionCandidates.forEach((moduleId)=>trackWarmUsage(`signal:session:${baseModule}:${moduleId}`));
    }, 650);
    return ()=>clearTimeout(t);
  }, [module_, farm?.id, session?.name, MODULE_PRIORITY_WARM_PLAN, getSessionWarmCandidates, getWarmControlDeps, runControlledPriorityWarm, trackWarmUsage, warmUsageBaseModuleKey]);

  const openModule = React.useCallback((mod, params={})=>{
    try { window.__fzLastOpenedModule = mod || ""; } catch(_) {}
    setModule(mod);
    setModuleSessionOverride(params?.sessionOverride || null);
    setModuleBridgeMeta(params?.sourceModule ? { sourceModule: params.sourceModule, forcedCostCenter: params?.forcedCostCenter || "" } : null);
    if(params?.openOrderId) setPurchaseOpenOrder(params.openOrderId);
    if(params?.openRecordId) setServiceOpenRecord(params.openRecordId);
    if(params?.openOccurrenceId) setOccurrenceOpenRecord(params.openOccurrenceId);
    if(params?.openInspectionId) setInspectionOpenRecord(params.openInspectionId);
    if(params?.poiPreset) setPoiPreset(params.poiPreset);
    if(params?.servicesTabPreset) setServicesTabPreset(params.servicesTabPreset);
    if(mod && farm?.id){
      try { window.dispatchEvent(new CustomEvent("fz:module:opened", { detail:{ moduleId:mod, farmId:farm.id } })); } catch(_) {}
    }
  }, [farm?.id]);

  useEffect(()=>{
    if(typeof window === "undefined" || !farm?.id) return;
    const dispatchPriorityRecordSync = (payload={}) => {
      const targetModule = String(payload?.module || "").trim().toLowerCase();
      const recordId = String(payload?.orderId || payload?.recordId || "").trim();
      const targetFarmId = String(payload?.farmId || farm.id || "").trim();
      const collection = collectionForPriorityModule(targetModule);
      if(!collection || !targetFarmId || !recordId) return;
      try {
        const dedupeKey = `${targetFarmId}:${collection}:${recordId}`;
        const now = Date.now();
        const lastKey = window.__fzLastLiveRecordSyncKey || "";
        const lastAt = Number(window.__fzLastLiveRecordSyncAt || 0);
        if(lastKey === dedupeKey && now - lastAt < 2500) return;
        window.__fzLastLiveRecordSyncKey = dedupeKey;
        window.__fzLastLiveRecordSyncAt = now;
      } catch(_) {}
      try {
        window.dispatchEvent(new CustomEvent("fz:live-activity-record-sync", {
          detail:{ module:targetModule, collection, recordId, farmId:targetFarmId, reason:"live_activity_deeplink" }
        }));
      } catch(_) {}
    };
    const openModuleFromLivePayload = (payload={}) => {
      try { window.__fzLastLiveDeepLink = { ...(payload || {}), receivedAt: new Date().toISOString() }; } catch(_) {}
      const targetModule = String(payload?.module || "").trim().toLowerCase();
      const recordId = String(payload?.orderId || payload?.recordId || "").trim();
      const targetFarmId = String(payload?.farmId || farm.id || "").trim();
      if(targetFarmId && String(targetFarmId)!==String(farm.id)) {
        return false;
      }
      if(!recordId) {
        return false;
      }
      const normalizedLivePayload = { ...payload, module: targetModule || "purchases", recordId, orderId: payload?.orderId || recordId, farmId: targetFarmId };
      beginLiveOpenHydration(normalizedLivePayload);
      dispatchPriorityRecordSync(normalizedLivePayload);
      if(targetModule === "purchases" || targetModule === "orders" || !targetModule){
        // v2.221.28: registra no servidor a rota efetivamente aberta após o toque da Live.
        const liveTouchBasePayload = {
          ...(payload || {}),
          collection: "orders",
          module: targetModule || "purchases",
          farmId: targetFarmId,
          recordId,
          orderId: recordId,
          routeOpened: "purchases",
          openOrderId: recordId,
          deviceId: getDeviceId?.(),
          sessionId: session?.sessionId || session?.id || "",
          accountId: session?.accountId || session?.id || session?.userId || "",
          userName: session?.name || session?.username || session?.userId || "",
          username: session?.username || session?.name || "",
          userId: session?.userId || session?.id || "",
          payload,
        };
        reportLiveTouchDiagnosticToServer({
          ...liveTouchBasePayload,
          stage: "farm_dashboard_open_purchases",
        }).catch(()=>{});
        // v2.221.29: a Live é enviada pelo servidor após confirmar o pedido.
        // Se o sync normal ainda não trouxe o pedido ao destinatário, hidrata localmente
        // como registro confirmado, nunca como PRV.
        (async () => {
          const hydrateKey = `${targetFarmId}:orders:${recordId}`;
          try {
            const now = Date.now();
            const lastKey = window.__fzLastLiveHydrateKey || "";
            const lastAt = Number(window.__fzLastLiveHydrateAt || 0);
            if (lastKey === hydrateKey && now - lastAt < 5000) return;
            window.__fzLastLiveHydrateKey = hydrateKey;
            window.__fzLastLiveHydrateAt = now;
          } catch (_) {}
          reportLiveTouchDiagnosticToServer({ ...liveTouchBasePayload, stage: "hydrate_request" }).catch(()=>{});
          updateLiveOpenHydration(recordId, { progress: 46, message: "Buscando registro confirmado no servidor..." });
          const res = await fetchLiveOrderHydration(liveTouchBasePayload);
          const data = res?.data || null;
          if (!res?.ok || !data?.found || !data?.record) {
            reportLiveTouchDiagnosticToServer({
              ...liveTouchBasePayload,
              stage: data?.found === false ? "hydrate_not_found" : "hydrate_error",
              hydrateStatus: res?.status || 0,
              hydrateError: res?.error || data?.error || "not_found_or_invalid_payload",
              hydrateResponse: data,
            }).catch(()=>{});
            updateLiveOpenHydration(recordId, { status: "loading", progress: 88, message: "Aguardando o pull_delta específico entregar o registro..." });
            return;
          }
          updateLiveOpenHydration(recordId, { progress: 78, message: "Aplicando dados recebidos no aparelho..." });
          const injected = await injectLiveHydratedOrderLocally({ farmId: targetFarmId, recordId, record: data.record });
          reportLiveTouchDiagnosticToServer({
            ...liveTouchBasePayload,
            stage: injected.injected ? "hydrate_local_injected" : "hydrate_local_skipped",
            hydrateSource: data.source || "",
            hydrateRecordNumber: data.record?.recordNumber || "",
            hydrateStatus: data.record?.status || "",
            hydrateResult: injected,
          }).catch(()=>{});
          updateLiveOpenHydration(recordId, { status: "done", progress: 100, message: "Dados recebidos do servidor." });
          clearLiveOpenHydrationSoon(recordId);
        })().catch((error)=>{
          reportLiveTouchDiagnosticToServer({
            ...liveTouchBasePayload,
            stage: "hydrate_exception",
            hydrateError: error?.message || String(error),
          }).catch(()=>{});
          updateLiveOpenHydration(recordId, { status: "error", progress: 90, message: "Não foi possível concluir a hidratação agora." });
        });
        openModule("purchases", { openOrderId: recordId });
        return true;
      }
      if(targetModule === "services"){
        // v3.8.04: abrir imediatamente com bloco de hidratação; não aguardar
        // resposta do servidor para montar a tela. O pull_delta específico já foi
        // solicitado acima e a hydrate direta acelera a chegada do registro.
        openModule("services", { openRecordId: recordId });
        (async () => {
          const hydrateKey = `${targetFarmId}:services:${recordId}`;
          let skippedByDedupe = false;
          try {
            const now = Date.now();
            const lastKey = window.__fzLastLiveHydrateKey || "";
            const lastAt  = Number(window.__fzLastLiveHydrateAt || 0);
            if (lastKey === hydrateKey && now - lastAt < 5000) skippedByDedupe = true;
            window.__fzLastLiveHydrateKey = hydrateKey;
            window.__fzLastLiveHydrateAt  = now;
          } catch (_) {}
          if (!skippedByDedupe) {
            const hydratePayload = {
              farmId: targetFarmId,
              recordId,
              activityKey: payload?.activityKey || "",
              collection: "services",
              module: "services",
              deviceId: getDeviceId?.(),
              userName: session?.name || session?.username || "",
              username: session?.username || session?.name || "",
              accountId: session?.accountId || session?.id || "",
            };
            updateLiveOpenHydration(recordId, { progress: 48, message: "Buscando serviço confirmado no servidor..." });
            const res  = await fetchLiveServiceHydration(hydratePayload);
            const data = res?.data || null;
            if (res?.ok && data?.found && data?.record) {
              updateLiveOpenHydration(recordId, { progress: 78, message: "Aplicando dados do serviço no aparelho..." });
              await injectLiveHydratedServiceLocally({ farmId: targetFarmId, recordId, record: data.record });
              updateLiveOpenHydration(recordId, { status: "done", progress: 100, message: "Dados recebidos do servidor." });
              clearLiveOpenHydrationSoon(recordId);
            } else {
              updateLiveOpenHydration(recordId, { status: "loading", progress: 88, message: "Aguardando o pull_delta específico entregar o serviço..." });
            }
          }
        })().catch(() => {
          updateLiveOpenHydration(recordId, { status: "error", progress: 90, message: "Não foi possível concluir a hidratação do serviço agora." });
        });
        return true;
      }
      if(targetModule === "occurrences" || targetModule === "occurrence"){
        // v3.8.04: abrir imediatamente e mostrar o bloco de hidratação enquanto
        // o hydrate/pull_delta específico atualiza a ocorrência.
        openModule("occurrences", { openOccurrenceId: recordId });
        (async () => {
          const hydrateKey = `${targetFarmId}:occurrences:${recordId}`;
          let skippedByDedupe = false;
          try {
            const now = Date.now();
            const lastKey = window.__fzLastLiveHydrateKey || "";
            const lastAt  = Number(window.__fzLastLiveHydrateAt || 0);
            if (lastKey === hydrateKey && now - lastAt < 5000) skippedByDedupe = true;
            window.__fzLastLiveHydrateKey = hydrateKey;
            window.__fzLastLiveHydrateAt  = now;
          } catch (_) {}
          if (!skippedByDedupe) {
            const hydratePayload = {
              farmId: targetFarmId,
              recordId,
              activityKey: payload?.activityKey || "",
              collection: "occurrences",
              module: "occurrences",
              deviceId: getDeviceId?.(),
              userName: session?.name || session?.username || "",
              username: session?.username || session?.name || "",
              accountId: session?.accountId || session?.id || "",
            };
            updateLiveOpenHydration(recordId, { progress: 48, message: "Buscando ocorrência confirmada no servidor..." });
            const res  = await fetchLiveOccurrenceHydration(hydratePayload);
            const data = res?.data || null;
            if (res?.ok && data?.found && data?.record) {
              updateLiveOpenHydration(recordId, { progress: 78, message: "Aplicando dados da ocorrência no aparelho..." });
              await injectLiveHydratedOccurrenceLocally({ farmId: targetFarmId, recordId, record: data.record });
              updateLiveOpenHydration(recordId, { status: "done", progress: 100, message: "Dados recebidos do servidor." });
              clearLiveOpenHydrationSoon(recordId);
            } else {
              updateLiveOpenHydration(recordId, { status: "loading", progress: 88, message: "Aguardando o pull_delta específico entregar a ocorrência..." });
            }
          }
        })().catch(() => {
          updateLiveOpenHydration(recordId, { status: "error", progress: 90, message: "Não foi possível concluir a hidratação da ocorrência agora." });
        });
        return true;
      }
      if(targetModule === "vet" || targetModule === "veterinary"){
        openModule("vet", { openRecordId: recordId });
        return true;
      }
      if(targetModule === "inspections" || targetModule === "inspection"){
        openModule("inspections", { openInspectionId: recordId });
        return true;
      }
      return false;
    };

    try{
      const url = new URL(window.location.href);
      const payload = {
        module: String(url.searchParams.get("module") || "").trim().toLowerCase(),
        orderId: String(url.searchParams.get("orderId") || url.searchParams.get("recordId") || "").trim(),
        farmId: String(url.searchParams.get("farmId") || "").trim(),
      };
      const notificationId = String(url.searchParams.get("notification") || "").trim();
      const opened = openModuleFromLivePayload(payload);
      if(payload.module || payload.orderId || notificationId || opened){
        url.searchParams.delete("module");
        url.searchParams.delete("orderId");
        url.searchParams.delete("recordId");
        url.searchParams.delete("farmId");
        url.searchParams.delete("notification");
        const nextUrl = `${url.pathname}${url.search}${url.hash}`;
        try{ window.history.replaceState({}, document.title, nextUrl || "/"); }catch(e){}
      }
    }catch(e){}

    try {
      const raw = sessionStorage.getItem("fz:pendingLiveActivityDeepLink");
      const payload = raw ? JSON.parse(raw) : null;
      if(openModuleFromLivePayload(payload)) sessionStorage.removeItem("fz:pendingLiveActivityDeepLink");
    } catch(_) {}

    const onLiveOpen = (event) => {
      try {
        if(openModuleFromLivePayload(event?.detail || {})) {
          sessionStorage.removeItem("fz:pendingLiveActivityDeepLink");
        }
      } catch(_) {}
    };
    window.addEventListener("fz:live-activity-open-record", onLiveOpen);
    return ()=>window.removeEventListener("fz:live-activity-open-record", onLiveOpen);
  }, [farm?.id, openModule, getDeviceId, session?.accountId, session?.id, session?.name, session?.sessionId, session?.userId, session?.username, beginLiveOpenHydration, updateLiveOpenHydration, clearLiveOpenHydrationSoon]);

  useEffect(()=>{
    if(isNativeGateLocked) return;
    if(!farm?.id || !notificationSyncSettings.enabled) return;
    let active = true;
    const initialRunner = runNotificationsSyncRef.current;
    if(notificationSyncSettings.checkOnAppOpen && initialRunner){
      Promise.resolve(initialRunner({ forceRemoteReads:true, triggerCollectionSync:false })).catch(()=>{});
    }
    if(!notificationSyncSettings.checkWhileUsingApp){
      return ()=>{ active = false; };
    }
    const timer = window.setInterval(()=>{
      if(!active) return;
      const runner = runNotificationsSyncRef.current;
      if(!runner) return;
      Promise.resolve(runner({ forceRemoteReads:true, triggerCollectionSync:false })).catch(()=>{});
    }, notificationSyncSettings.pollingMinutes * 60 * 1000);
    return ()=>{ active = false; window.clearInterval(timer); };
  }, [farm?.id, notificationSyncSettings.checkOnAppOpen, notificationSyncSettings.checkWhileUsingApp, notificationSyncSettings.enabled, notificationSyncSettings.pollingMinutes, isNativeGateLocked]);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    async function loadDashboardClimate({ showLoading = true } = {}) {
      if (!farmId) {
        setDashboardClimateSummary(null);
        return;
      }
      if (showLoading) setDashboardClimateLoading(true);
      try {
        const data = await fetchFarmClimateSummary({
          farmId,
          preferredSource: farmClimateProfile?.preferredSource || climateGlobalProfile?.preferredSource || "wunderground",
          fallbackToInmet: farmClimateProfile?.fallbackToInmet !== false,
          stationCandidates: dashboardClimateStations,
          autoSync: true,
          userName: session?.name || "",
        });
        if (!cancelled) setDashboardClimateSummary(data || null);
      } catch {
        if (!cancelled) setDashboardClimateSummary(null);
      } finally {
        if (!cancelled && showLoading) setDashboardClimateLoading(false);
      }
    }
    loadDashboardClimate({ showLoading: true });
    timer = window.setInterval(() => {
      loadDashboardClimate({ showLoading: false });
    }, 20 * 60 * 1000);
    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, [farmId, farmClimateProfile?.preferredSource, farmClimateProfile?.fallbackToInmet, climateGlobalProfile?.preferredSource, dashboardClimateStations, session?.name]);

  const activeSession = moduleSessionOverride || session;
  const visibleShortcutChoices = useMemo(()=>SHORTCUT_MODULE_CHOICES.filter(moduleId=>allowedMods.some(m=>m.id===moduleId)), [SHORTCUT_MODULE_CHOICES, allowedMods]);
  const quickShortcutModules = useMemo(()=>{
    const selected = (currentUiPreferences.shortcutModules || []).filter(moduleId=>visibleShortcutChoices.includes(moduleId));
    const fallback = visibleShortcutChoices.filter(moduleId=>!selected.includes(moduleId));
    return [...selected, ...fallback].slice(0,4);
  }, [currentUiPreferences.shortcutModules, visibleShortcutChoices]);
  const shortcutBarTint = resolveShortcutBarTint(farm?.color, T.accent || "#3B82F6");
  const shortcutBarIsWarmRed = shortcutBarTint === "#991B1B";
  const shortcutBarSurfaceMix = shortcutBarIsWarmRed ? 20 : 14;
  const shortcutBarButtonMix = shortcutBarIsWarmRed ? 36 : 30;
  const shortcutBarIconColor = getContrastTextColor(shortcutBarTint);

  // v3.10.03: capturar bootstrapFarmCollections em ref — evita que syncCtx
  // inteiro nas deps cause reexecução do efeito a cada render do SyncProvider.
  // bootstrapState muda várias vezes durante o bootstrap → syncCtx recria → loop.
  const _bootstrapFarmCollections = syncCtx?.bootstrapFarmCollections ?? null;
  const _bootstrapFnRef = useRef(null);
  useEffect(() => {
    _bootstrapFnRef.current = _bootstrapFarmCollections;
  }, [_bootstrapFarmCollections]);

  // runKey por farmId + session.name — o efeito só roda uma vez por combinação.
  // Troca de fazenda ou de usuário reseta automaticamente no cleanup.
  const _bootstrapEffectKeyRef = useRef("");

  useEffect(() => {
    if (!farm?.id) return;
    if (isNativeGateLocked) return;
    const _runKey = `${farm.id}:${session?.name || ""}`;
    if (_bootstrapEffectKeyRef.current === _runKey) return;
    _bootstrapEffectKeyRef.current = _runKey;
    let cancelled = false;
    (async () => {
      try {
        await _bootstrapFnRef.current?.(farm.id, session?.name || "sistema");
      } catch (_) {}
      if (cancelled) return;
      try {
        await syncFarmCollaboratorAccountsToEmployees(farm.id, session?.name || "sistema");
      } catch (_) {}
    })();
    const rerun = () => {
      if (cancelled) return;
      // Cooldown de 2 minutos — evita bootstrap duplicado por fz:online:restored.
      try {
        const lastAt = Number(window[`__fzBootstrapAt:${farm.id}`] || 0);
        if (lastAt && Date.now() - lastAt < 2 * 60 * 1000) return;
      } catch (_) {}
      _bootstrapFnRef.current?.(farm.id, session?.name || "sistema").catch(() => {});
    };
    window.addEventListener("fz:online:restored", rerun);
    return () => {
      cancelled = true;
      window.removeEventListener("fz:online:restored", rerun);
      // Resetar runKey ao desmontar — permite rerodar ao trocar farm/session.
      if (_bootstrapEffectKeyRef.current === _runKey) _bootstrapEffectKeyRef.current = "";
    };
  }, [farm?.id, session?.name, isNativeGateLocked, syncFarmCollaboratorAccountsToEmployees]);

  useEffect(()=>{
    if(!farm?.id) return;
    if(isNativeGateLocked) return;
    const syncCollabs=()=>syncFarmCollaboratorAccountsToEmployees(farm.id, session?.name||"sistema").catch(()=>{});
    syncCollabs();
    const onSyncUpdated=(e)=>{
      if(!e.detail?.key || e.detail.key==="auth:accounts") syncCollabs();
    };
    window.addEventListener("fz:sync:updated", onSyncUpdated);
    return ()=>window.removeEventListener("fz:sync:updated", onSyncUpdated);
  },[farm?.id, session?.name, syncFarmCollaboratorAccountsToEmployees, isNativeGateLocked]);

  useEffect(()=>{
    if(isNativeGateLocked) return undefined;
    let cancelled = false;
    (async()=>{
      try{
        if(typeof Notification === "undefined"){
          await registerNotificationDevice("unsupported");
          return;
        }
        let permission = Notification.permission;
        const askKey = `fz:notif:asked:${notificationDeviceId}:${session?.id || "anon"}`;
        if(permission === "default" && !localStorage.getItem(askKey)){
          localStorage.setItem(askKey, new Date().toISOString());
          try{ permission = await Notification.requestPermission(); }catch(e){}
        }
        if(cancelled) return;
        await registerNotificationDevice(permission || Notification.permission || "default");
      }catch(e){}
    })();
    return ()=>{ cancelled = true; };
  }, [registerNotificationDevice, notificationDeviceId, session?.id, isNativeGateLocked]);

  useEffect(()=>{
    const accountId = String(session?.id || "").trim();
    const userId = String(session?.userId || "").trim();
    const username = String(session?.username || "").trim().toLowerCase();
    if(!accountId && !userId && !username) return;
    const deliveredKey = `fz:notif:delivered:${notificationDeviceId}`;
    let delivered = [];
    try{ delivered = JSON.parse(localStorage.getItem(deliveredKey) || "[]"); }catch(e){ delivered = []; }
    const deliveredSet = new Set(Array.isArray(delivered) ? delivered : []);
    const list = Array.isArray(notifications) ? notifications : [];
    const pending = list.filter((item)=>{
      if(!item || !["purchase_event","service_event","occurrence_event","inspection_event","veterinary_event"].includes(String(item.type || ""))) return false;
      const recipients = Array.isArray(item.recipients) ? item.recipients : [];
      const mine = recipients.some((recipient)=>{
        const rid = String(recipient?.accountId || "").trim();
        const ruid = String(recipient?.userId || "").trim();
        const run = String(recipient?.username || "").trim().toLowerCase();
        return (rid && rid === accountId) || (ruid && ruid === userId) || (run && run === username);
      });
      const alreadyRead = getNotificationReadMeta(item).isRead;
      return mine && !alreadyRead && !deliveredSet.has(String(item.id || ""));
    });
    if(!pending.length) return;
    (async()=>{
      for(const item of pending){
        const shown = await showSystemNotification({
          id: item.id,
          tag: `purchase:${item.id}`,
          title: item.title || (item.orderRecordNumber ? `${item.type === "occurrence_event" ? "Ocorrências" : item.type === "service_event" ? "Serviços" : item.type === "inspection_event" ? "Vistorias" : item.type === "veterinary_event" ? "Veterinário" : "Compras"} · ${item.orderRecordNumber}` : (item.type === "occurrence_event" ? "Ocorrências" : item.type === "service_event" ? "Serviços" : item.type === "inspection_event" ? "Vistorias" : item.type === "veterinary_event" ? "Veterinário" : "Compras")),
          body: item.summary || item.body || `Você recebeu uma atualização em ${item.type === "occurrence_event" ? "ocorrências" : item.type === "service_event" ? "serviços" : item.type === "inspection_event" ? "vistorias" : item.type === "veterinary_event" ? "veterinário" : "compras"}.`,
          url: `/?notification=${encodeURIComponent(String(item.id || ""))}`,
        });
        if(shown){
          deliveredSet.add(String(item.id || ""));
        }
      }
      try{ localStorage.setItem(deliveredKey, JSON.stringify(Array.from(deliveredSet).slice(-300))); }catch(e){}
    })();
  }, [getNotificationReadMeta, notifications, notificationDeviceId, session?.id, session?.userId, session?.username, showSystemNotification]);

  const fuelAlerts     = notifications.filter(n=>n.type==="fuel_low"&&!n.resolved).length;

  // Card visibility — admin/gestor see all; collaborators see only allowed cards
  const allowedCards = session?.allowedCards||[];
  const canSeeCard = (cardId) => {
    if(isGestor||session?.role==="admin"||session?.role==="gestor") return true;
    return allowedCards.includes(cardId);
  };
  const current        = MODULES.find(m=>m.id===module_);
  const cachedModuleHeaderMenuMeta = React.useMemo(()=>{
    if (!module_) return null;
    const cached = moduleHeaderMenuCache?.[module_] || null;
    if (!cached || !Array.isArray(cached.items) || cached.items.length===0) return null;
    return cached;
  }, [moduleHeaderMenuCache, module_]);
  const fallbackModuleHeaderMenuMeta = React.useMemo(()=>{
    if (!module_) return null;
    if (module_ === "vet") {
      return { title:"Menu do Veterinário", compact:false, items:[{
        id:"vet-list",
        label:"Atendimentos",
        icon:"🏥",
        active:true,
        onClick:()=>window.dispatchEvent(new CustomEvent("fz:app:navigate-back-to-module-start")),
        children:[
          { id:"vet-list-individual", label:"Individuais", icon:"🐄", active:true, onClick:()=>window.dispatchEvent(new CustomEvent("fz:vet:view-individual")) },
          { id:"vet-list-lot", label:"Lotes", icon:"📦", onClick:()=>window.dispatchEvent(new CustomEvent("fz:vet:view-lot")) },
          { id:"vet-list-agenda", label:"Agenda", icon:"🗓️", onClick:()=>window.dispatchEvent(new CustomEvent("fz:vet:view-agenda")) },
        ]
      }] };
    }
    if (module_ === "sede") {
      return { title:"Menu da Sede", compact:false, items:[
        { id:"overview", label:"Resumo", icon:"🏠", active:true },
        { id:"locations", label:"Localização", icon:"🗺️" },
        { id:"purchases", label:"Compras", icon:"🛒" },
        { id:"internal", label:"Serviços Internos", icon:"🔨" },
        { id:"external", label:"Serviços Terceirizados", icon:"👷" },
        { id:"occurrences", label:"Ocorrências", icon:"🚨" },
        { id:"inspections", label:"Vistorias", icon:"🔍" },
      ] };
    }
    if (module_ === "services") return { title:"Menu de Serviços", compact:false, items:[] };
    if (module_ === "services_external") return { title:"Menu de Serviços Terceirizados", compact:false, items:[] };
    if (module_ === "occurrences") return { title:"Menu de Ocorrências", compact:false, items:[] };
    if (module_ === "inspections") return null;
    if (module_ === "animals") return { title:"Menu da Pecuária", compact:false, items:[] };
    return null;
  }, [module_]);
  const effectiveModuleHeaderMenuMeta = moduleHeaderMenuMeta || cachedModuleHeaderMenuMeta || fallbackModuleHeaderMenuMeta;
  const normalizeParticipantName = React.useCallback((value) => String(typeof value === "string" ? value : (value?.name || value || "")).trim().toLowerCase(), []);
  const myEmployeeForDashboard = React.useMemo(()=>{
    const sessionId = String(session?.id || "").trim();
    const sessionUserId = String(session?.userId || "").trim();
    const sessionUsername = String(session?.username || "").trim().toLowerCase();
    const sessionNameNorm = normalizeParticipantName(session?.name || "");
    return (employees||[]).find(emp=>{
      const linked = String(emp?.linkedAccountId || emp?.accountId || "").trim();
      const userId = String(emp?.userId || "").trim();
      const username = String(emp?.username || "").trim().toLowerCase();
      const nameNorm = normalizeParticipantName(emp?.name || "");
      return (sessionId && linked===sessionId) || (sessionUserId && userId===sessionUserId) || (sessionUsername && username===sessionUsername) || (sessionNameNorm && nameNorm===sessionNameNorm);
    }) || null;
  }, [employees, normalizeParticipantName, session?.id, session?.userId, session?.username, session?.name]);
  const dashboardIdentity = React.useMemo(()=>({
    accountId: String(session?.id || "").trim(),
    userId: String(session?.userId || "").trim(),
    employeeId: String(myEmployeeForDashboard?.id || "").trim(),
    linkedAccountId: String(myEmployeeForDashboard?.linkedAccountId || myEmployeeForDashboard?.accountId || "").trim(),
    username: String(session?.username || myEmployeeForDashboard?.username || "").trim().toLowerCase(),
    name: normalizeParticipantName(session?.name || myEmployeeForDashboard?.name || ""),
  }), [myEmployeeForDashboard, normalizeParticipantName, session?.id, session?.userId, session?.username, session?.name]);
  const participantMatchesDashboardIdentity = React.useCallback((value)=>{
    if(!value) return false;
    const obj = (value && typeof value === "object") ? value : { name: value };
    const ids = [obj?.id, obj?.employeeId, obj?.userId, obj?.linkedAccountId, obj?.accountId].map(v=>String(v||"").trim()).filter(Boolean);
    if(ids.some(v=> v===dashboardIdentity.employeeId || v===dashboardIdentity.accountId || v===dashboardIdentity.userId || v===dashboardIdentity.linkedAccountId)) return true;
    const usernames = [obj?.username].map(v=>String(v||"").trim().toLowerCase()).filter(Boolean);
    if(usernames.includes(dashboardIdentity.username)) return true;
    const names = [obj?.name, obj?.displayName, obj].map(normalizeParticipantName).filter(Boolean);
    return names.includes(dashboardIdentity.name);
  }, [dashboardIdentity, normalizeParticipantName]);

  const dashboardBridgeCostCenter = String(session?.sedeBridge?.enabled ? (session?.sedeBridge?.costCenter || "") : "").trim();
  const dashboardBridgeScoped = !!dashboardBridgeCostCenter;
  const matchesDashboardBridgeCostCenter = React.useCallback((item)=> !dashboardBridgeScoped || String(item?.costCenter || "").trim().toLowerCase()===dashboardBridgeCostCenter.toLowerCase(), [dashboardBridgeScoped, dashboardBridgeCostCenter]);
  const isOwnServiceForDashboard = React.useCallback((item)=>{
    if(isGestor || session?.role==="admin" || session?.role==="gestor") return true;
    const assignees = Array.isArray(item?.assignees) ? item.assignees : [];
    return assignees.some(participantMatchesDashboardIdentity)
      || participantMatchesDashboardIdentity({ id:item?.assignedToId, employeeId:item?.assignedToId, name:item?.assignedTo, username:item?.assignedToUsername })
      || participantMatchesDashboardIdentity({ id:item?.contactPersonId, employeeId:item?.contactPersonId, name:item?.contactPerson, username:item?.contactPersonUsername });
  }, [isGestor, session?.role, participantMatchesDashboardIdentity]);
  const canViewServiceInDashboard = React.useCallback((svc, moduleId)=>{
    if(!svc) return false;
    if(isGestor || session?.role==="admin" || session?.role==="gestor") return true;
    const hasCostCenterAccess = itemMatchesCostCenterAccess(session, moduleId, svc);
    if(session?.role === "colaborador") return isOwnServiceForDashboard(svc) || hasCostCenterAccess;
    return hasCostCenterAccess;
  }, [isGestor, session, itemMatchesCostCenterAccess, isOwnServiceForDashboard]);

  useEffect(()=>{
    const handleRegister = (event)=>{
      const detail = event?.detail || null;
      if (!detail || !Array.isArray(detail.items) || detail.items.length===0) {
        setModuleHeaderMenuMeta(null);
        return;
      }
      const normalizedDetail = {
        title: detail.title || "Menu do módulo",
        compact: !!detail.compact,
        items: detail.items,
      };
      setModuleHeaderMenuMeta(normalizedDetail);
      setModuleHeaderMenuCache(prev=>{
        if (!module_) return prev;
        return { ...prev, [module_]: normalizedDetail };
      });
    };
    const handleClear = ()=>setModuleHeaderMenuMeta(null);
    const handleAppHome = ()=>{
      setFarmNavOpen(false);
      setModule(null);
      setModuleSessionOverride(null);
      setModuleBridgeMeta(null);
      setPurchaseOpenOrder(null);
      setPoiPreset(null);
      setServicesTabPreset(null);
    };
    const handleAppBack = ()=>{
      if (!module_) return;
      const hasLiveMenu = !!moduleHeaderMenuMeta;
      const hasCachedMenu = !!cachedModuleHeaderMenuMeta;
      if (!hasLiveMenu && hasCachedMenu) {
        setFarmNavOpen(false);
        setPurchaseOpenOrder(null);
        setPoiPreset(null);
        setServicesTabPreset(null);
        setModuleHeaderMenuMeta(null);
        setModuleRenderVersion(v=>v+1);
        return;
      }
      setFarmNavOpen(false);
      setModule(null);
      setModuleSessionOverride(null);
      setModuleBridgeMeta(null);
      setPurchaseOpenOrder(null);
      setPoiPreset(null);
      setServicesTabPreset(null);
    };
    const handleModuleGoHome = (event)=>{
      if (!module_) return;
      event?.detail?.markHandled?.();
      setFarmNavOpen(false);
      setModule(null);
      setModuleSessionOverride(null);
      setModuleBridgeMeta(null);
      setPurchaseOpenOrder(null);
      setPoiPreset(null);
      setServicesTabPreset(null);
      setModuleHeaderMenuMeta(null);
    };
    window.addEventListener("fz:module-header-menu:register", handleRegister);
    window.addEventListener("fz:module-header-menu:clear", handleClear);
    window.addEventListener("fz:app:navigate-back", handleAppBack);
    window.addEventListener("fz:app:navigate-home", handleAppHome);
    window.addEventListener("fz:module-header-menu:go-home", handleModuleGoHome);
    return ()=>{
      window.removeEventListener("fz:module-header-menu:register", handleRegister);
      window.removeEventListener("fz:module-header-menu:clear", handleClear);
      window.removeEventListener("fz:app:navigate-back", handleAppBack);
      window.removeEventListener("fz:app:navigate-home", handleAppHome);
      window.removeEventListener("fz:module-header-menu:go-home", handleModuleGoHome);
    };
  },[module_, moduleHeaderMenuMeta, cachedModuleHeaderMenuMeta, setPurchaseOpenOrder, setPoiPreset, setServicesTabPreset]);

  useEffect(()=>{
    if(!module_) setModuleHeaderMenuMeta(null);
  },[module_]);

  if(!farm){
    return <div style={{minHeight:"100vh",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",color:T.muted,fontFamily:"'DM Sans',sans-serif"}}>Carregando fazenda...</div>;
  }
  const farmMenuItems = [...allowedMods]
    .sort((a,b)=>String(a?.label||"{}").localeCompare(String(b?.label||""), "pt-BR", { sensitivity:"base", numeric:true }))
    .map(m=>({
      id:m.id,
      label:m.label,
      icon:m.icon,
      color:m.color,
      desc:m.desc,
    }));
  const dashboardOrders = isNativeOperationalDataHidden ? [] : (orders || []);
  const dashboardServices = isNativeOperationalDataHidden ? [] : (services || []);
  const dashboardOccurrences = isNativeOperationalDataHidden ? [] : (occurrences || []);
  const dashboardStock = isNativeOperationalDataHidden ? [] : (stock || []);
  const dashboardStockMovements = isNativeOperationalDataHidden ? [] : (stockMovements || []);

  const visibleInternalServicesForDashboard = dashboardServices
    .filter(s=>!(s && (s._deleted || s.deletedAt || s.deleted_at)))
    .filter(s=>normalizeServiceType(s)!=="external")
    .filter(matchesDashboardBridgeCostCenter)
    .filter(s=>canViewServiceInDashboard(s, "services"));
  const visibleExternalServicesForDashboard = dashboardServices
    .filter(s=>!(s && (s._deleted || s.deletedAt || s.deleted_at)))
    .filter(s=>normalizeServiceType(s)==="external")
    .filter(matchesDashboardBridgeCostCenter)
    .filter(s=>canViewServiceInDashboard(s, "services_external"));
  const closedOccurrenceStatusesDashboard = new Set(["Concluída","Cancelada","Concluído","Cancelado"]);
  const visibleOccurrencesForDashboard = dashboardOccurrences
    .filter(o=>!((o&& (o._deleted || o.deletedAt || o.deleted_at))))
    .filter(matchesDashboardBridgeCostCenter)
    .filter(o=>itemMatchesCostCenterAccess(session, "occurrences", o));
  const svcOpen = visibleInternalServicesForDashboard
    .filter(s=>!["Concluído","Cancelado","Negado"].includes(s.status));
  const occOpen = visibleOccurrencesForDashboard
    .filter(o=>!closedOccurrenceStatusesDashboard.has(String(o?.status||"").trim()));
  const extOpen = visibleExternalServicesForDashboard
    .filter(s=>!["Concluído","Cancelado","Negado"].includes(s.status));
  const normalizePurchaseItemStatusForDashboard = (itemStatus, orderStatus="") => {
    const value = String(itemStatus || "").trim();
    if (["Analisando","Aprovado","Negado","Parcial"].includes(value)) return value;
    const order = String(orderStatus || "").trim();
    if (["Negado","Cancelado"].includes(order)) return "Negado";
    if (["Aprovado","Em Trânsito","Concluído"].includes(order)) return "Aprovado";
    return "Analisando";
  };
  const orderHasPendingApprovalForDashboard = (order) => {
    if (!order) return false;
    const orderStatus = String(order?.status || "").trim();
    if (orderStatus === "Pendente") return true;
    if (["Concluído","Cancelado","Negado"].includes(orderStatus)) return false;
    const items = Array.isArray(order?.items) ? order.items : [];
    if (!items.length) return ["Pendente","Em Análise"].includes(orderStatus);
    return items.some((item)=>normalizePurchaseItemStatusForDashboard(item?.itemStatus || item?.status, orderStatus) === "Analisando");
  };
  const visibleOrdersForDashboard = dashboardOrders
    .filter(o=>!(o && (o._deleted || o.deletedAt || o.deleted_at)))
    .filter(matchesDashboardBridgeCostCenter)
    .filter(o=>itemMatchesCostCenterAccess(session, "purchases", o));
  const purchaseOpen   = visibleOrdersForDashboard.filter(o=>!["Concluído","Cancelado","Negado"].includes(String(o?.status||"").trim())).length;
  const purchaseNeedsApproval = visibleOrdersForDashboard.filter(orderHasPendingApprovalForDashboard).length;
  const purchaseInTransit = visibleOrdersForDashboard.filter(o=>String(o?.status||"").trim()==="Em Trânsito").length;
  const visibleStockForDashboard = dashboardStock
    .filter(item=>matchesDashboardBridgeCostCenter(item))
    .filter(item=>canViewCostCenter(session, "stock", item?.costCenter || ""));
  const stockUsageMap = dashboardStockMovements.reduce((acc,mv)=>{
    if(mv?.type!=="saida"||mv?.itemId==null) return acc;
    const key=String(mv.itemId);
    acc[key]=(acc[key]||0)+Number(mv.qty||0);
    return acc;
  },{});
  const topLowStockItems = [...visibleStockForDashboard]
    .filter(i=>(Number(i.quantity)||0) <= (Number(i.minQty)||0))
    .sort((a,b)=>((stockUsageMap[String(b.id)]||0)-(stockUsageMap[String(a.id)]||0)) || ((Number(a.quantity)||0)-(Number(b.quantity)||0)) || String(a.name||"").localeCompare(String(b.name||""),"pt-BR"))
    .slice(0,3);
  const stockLowUsageTotal = topLowStockItems.reduce((sum,item)=>sum+(stockUsageMap[String(item.id)]||0),0);

  const bootstrapGateActive = !!farm?.id && !isNativeGateLocked && (!bootstrapState || (bootstrapState.status && bootstrapState.status !== "ready"));
  if (bootstrapGateActive) {
    return (
      <BootstrapGateScreen
        farm={farm}
        state={bootstrapState || { status: "not_started", progress: 0 }}
        T={T}
        onRetry={retryBootstrap ? () => retryBootstrap(farmId) : null}
      />
    );
  }

  return <div className={nativeFixedTop ? "fz-native-scroll-page" : undefined} style={{minHeight:nativeFixedTop?"100dvh":"100vh",height:nativeFixedTop?"100dvh":undefined,background:T.bg,fontFamily:"'DM Sans',sans-serif",color:T.text,overflowX:"hidden",overflowY:nativeFixedTop?"auto":undefined,WebkitOverflowScrolling:nativeFixedTop?"touch":undefined,overscrollBehaviorY:nativeFixedTop?"contain":undefined,position:"relative"}}>
    {farmNavOpen && !module_ && <div onClick={()=>setFarmNavOpen(false)} style={{position:"fixed",inset:0,background:"rgba(2,6,23,.22)",backdropFilter:"blur(2px)",zIndex:54}}/>}
    {/* Header */}
    <BootstrapProgressBar
      state={bootstrapState}
      T={T}
      onRetry={retryBootstrap ? () => retryBootstrap(farmId) : null}
    />
    <div className={`fz-app-header${nativeFixedTop ? " fz-native-fixed-header" : ""}`} style={{
      background:T.card, borderBottom:`1px solid ${T.border}`,
      display:"flex", alignItems:"center", padding:"0 16px",
      paddingTop:"max(12px, env(safe-area-inset-top))",
      paddingLeft:"max(16px, env(safe-area-inset-left))",
      paddingRight:"max(16px, env(safe-area-inset-right))",
      minHeight:56, justifyContent:"space-between",
      position:nativeFixedTop?"fixed":"sticky", top:0, left:nativeFixedTop?0:undefined, right:nativeFixedTop?0:undefined, zIndex:50,
    }}>
      <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0,flex:1}}>
        {!module_ && (
          <button
            type="button"
            onClick={()=>setFarmNavOpen(v=>!v)}
            aria-label="Abrir menu de módulos"
            style={{width:31,height:31,borderRadius:11,border:`1px solid ${farmNavOpen?T.accent:T.border}`,background:farmNavOpen?`${T.accent}14`:T.card2,color:farmNavOpen?T.accent:T.text,display:"grid",placeItems:"center",cursor:"pointer",boxShadow:farmNavOpen?`0 0 0 1px ${T.accent}1f inset`:"none",flex:"0 0 auto",marginLeft:-4}}
          >
            <div style={{display:"flex",flexDirection:"column",gap:3}}>
              <span style={{display:"block",width:13,height:2,borderRadius:99,background:"currentColor"}}/>
              <span style={{display:"block",width:13,height:2,borderRadius:99,background:"currentColor"}}/>
              <span style={{display:"block",width:13,height:2,borderRadius:99,background:"currentColor"}}/>
            </div>
          </button>
        )}
        {module_ ? (
          <button
            type="button"
            onClick={()=>window.dispatchEvent(new CustomEvent("fz:module-header-menu:toggle"))}
            aria-label={effectiveModuleHeaderMenuMeta?.title || "Abrir menu do módulo"}
            style={{width:31,height:31,borderRadius:11,border:`1px solid ${T.border}`,background:T.card2,color:T.text,display:"grid",placeItems:"center",cursor:"pointer",flex:"0 0 auto",marginLeft:-4}}
          >
            <div style={{display:"flex",flexDirection:"column",gap:3}}>
              <span style={{display:"block",width:13,height:2,borderRadius:99,background:"currentColor"}}/>
              <span style={{display:"block",width:13,height:2,borderRadius:99,background:"currentColor"}}/>
              <span style={{display:"block",width:13,height:2,borderRadius:99,background:"currentColor"}}/>
            </div>
          </button>
        ) : null}
        <FarmEmoji farm={farm} size={22} />
        <div style={{minWidth:0}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700,color:T.text,lineHeight:1}}>{current?current.label:farm.name}</div>
          {!current&&<div style={{fontSize:11,color:T.muted}}>Sistema de Gestão</div>}
        </div>
      </div>
      <div className="fz-app-header-right" style={{display:"flex",alignItems:"center",gap:8}}>
        {fuelAlerts>0&&!module_&&<div onClick={()=>setModule("machinery")} style={{background:`${T.yellow}22`,color:T.yellow,border:`1px solid ${T.yellow}44`,borderRadius:8,padding:"4px 12px",fontSize:12,fontWeight:600,cursor:"pointer"}}>⛽ {fuelAlerts} alerta{fuelAlerts>1?"s":""} combustível</div>}
        <SyncStatusBar inline activeModule={module_} farmId={farmId}/>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <button
            type="button"
            onClick={()=>setNotificationsCenterOpen(true)}
            title={unreadNotificationCount>0 ? `${unreadNotificationCount} notificação(ões) não lida(s)` : "Central de notificações"}
            aria-label="Abrir central de notificações"
            style={{
              position:"relative",
              width:32,height:32,borderRadius:"50%",
              border:`1px solid ${unreadNotificationCount>0 ? `${T.accent}88` : T.border}`,
              background:unreadNotificationCount>0 ? `${T.accent}18` : T.card2,
              color:unreadNotificationCount>0 ? T.accent : T.muted,
              display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,cursor:"pointer",
              animation:unreadNotificationCount>0 ? "fzPulse 1.4s ease-in-out infinite" : "none",
              boxShadow:unreadNotificationCount>0 ? `0 0 0 1px ${T.accent}22 inset` : "none",
            }}
          ><img src={notificationMailIcon} alt="" aria-hidden="true" style={{width:22,height:22,display:"block",objectFit:"contain"}} />
            {unreadNotificationCount>0 ? (
              <span style={{position:"absolute",top:-4,right:-4,minWidth:18,height:18,padding:"0 5px",borderRadius:999,background:T.red,color:"#fff",fontSize:10,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1,border:`2px solid ${T.card}`}}>
                {unreadNotificationCount>9 ? "9+" : unreadNotificationCount}
              </span>
            ) : null}
          </button>
          <div
            style={{width:32,height:32,borderRadius:"50%",background:`${T.accent}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,cursor:"pointer"}}
            onClick={()=>{
              if (session?.role === "admin") {
                onBack && onBack();
                return;
              }
              setModule("meu_perfil");
            }}
            title={session?.role === "admin" ? "Voltar ao Painel Admin" : "Meu perfil"}
          >👤</div>
          <button onClick={onLogout||onBack} style={{background:"none",border:`1px solid ${T.border}`,color:T.muted,cursor:"pointer",borderRadius:8,padding:"5px 10px",fontSize:12}}>Sair</button>
        </div>
      </div>
    </div>
    {nativeFixedTop ? <div aria-hidden="true" style={{height:nativeHeaderSpacer, flex:"0 0 auto"}}/> : null}

    {isNativeGateLocked && (
      <div style={{padding:nativeFixedTop?"10px 14px":"10px 14px 0"}}>
        <div style={{
          background: nativeDeviceStatus === "revoked" ? "rgba(239,68,68,.10)" : "rgba(245,158,11,.12)",
          border: `1px solid ${nativeDeviceStatus === "revoked" ? "rgba(239,68,68,.35)" : "rgba(245,158,11,.35)"}`,
          borderRadius:14,padding:"14px 16px",display:"grid",gap:12
        }}>
          <div style={{display:"flex",gap:12,alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap"}}>
            <div style={{flex:"1 1 220px"}}>
              <div style={{fontWeight:900,color:T.text,marginBottom:4,fontSize:14}}>{nativeGateMessage.title}</div>
              <div style={{fontSize:13,color:T.muted,lineHeight:1.6,whiteSpace:"pre-line"}}>{nativeGateMessage.body}</div>
            </div>
            {nativeDeviceVerification?.code && nativeDeviceStatus !== "revoked" && (
              <div style={{fontSize:22,fontWeight:900,letterSpacing:3,color:T.text,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"10px 14px",textAlign:"center",flexShrink:0}}>
                {nativeDeviceVerification.code}
              </div>
            )}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            <Btn size="sm" variant="ghost" onClick={handleCheckNativeApproval} disabled={nativeApprovalButtonDisabled}>{nativeApprovalButtonLabel}</Btn>
            <div style={{fontSize:11,color:T.muted}}>Verificando aprovação automaticamente a cada 30 segundos.</div>
          </div>
          {nativeApprovalMessage ? <div style={{fontSize:12,color:T.muted,paddingTop:2}}>{nativeApprovalMessage}</div> : null}
        </div>
      </div>
    )}

    {module_ && !moduleHeaderMenuMeta && effectiveModuleHeaderMenuMeta && (
      <ModuleHeaderMenu
        T={T}
        title={effectiveModuleHeaderMenuMeta.title || "Menu do módulo"}
        compact={!!effectiveModuleHeaderMenuMeta.compact}
        items={effectiveModuleHeaderMenuMeta.items}
        onBack={effectiveModuleHeaderMenuMeta.onBack}
      />
    )}

    <Modal open={notificationsCenterOpen} onClose={()=>setNotificationsCenterOpen(false)} title="Central de notificações">
      <div style={{display:"grid",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <div style={{fontSize:12,color:T.muted}}>As notificações internas de Compras, Serviços, Serviços Terceirizados, Ocorrências, Vistorias e Veterinário aparecem aqui para admin, criador do registro e gestor habilitado.</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <Badge color={unreadNotificationCount>0?T.orange:T.green}>{unreadNotificationCount>0?`${unreadNotificationCount} não lida(s)`:"Tudo lido"}</Badge>
            {notificationReadsLoading ? <Badge color={T.blue}>Sincronizando leituras…</Badge> : null}
            <Btn size="sm" variant="ghost" onClick={markAllNotificationsRead} disabled={!filteredGroupedNotifications.length || notificationReadsLoading}>Marcar tudo como lido</Btn>
          </div>
        </div>
        <div style={{display:"grid",gap:10,padding:12,borderRadius:14,border:`1px solid ${T.border}`,background:T.card2}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))",gap:10}}>
            <input value={notificationFilters.query} onChange={(e)=>updateNotificationFilter("query", e.target.value)} placeholder="Pesquisar por módulo, assunto, fazenda ou status" style={{minWidth:0,width:"100%",padding:"10px 12px",borderRadius:12,border:`1px solid ${T.border}`,background:T.card,color:T.text,fontSize:13}} />
            <select value={notificationFilters.moduleKey} onChange={(e)=>updateNotificationFilter("moduleKey", e.target.value)} style={{minWidth:0,width:"100%",padding:"10px 12px",borderRadius:12,border:`1px solid ${T.border}`,background:T.card,color:T.text,fontSize:13}}>
              {notificationModuleOptions.map((option)=><option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <select value={notificationFilters.datePreset} onChange={(e)=>updateNotificationFilter("datePreset", e.target.value)} style={{minWidth:0,width:"100%",padding:"10px 12px",borderRadius:12,border:`1px solid ${T.border}`,background:T.card,color:T.text,fontSize:13}}>
              <option value="all">Todas as datas</option>
              <option value="today">Hoje</option>
              <option value="7d">Últimos 7 dias</option>
              <option value="30d">Últimos 30 dias</option>
              <option value="custom">Período personalizado</option>
            </select>
          </div>
          {notificationFilters.datePreset === "custom" ? (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))",gap:10}}>
              <input type="date" value={notificationFilters.startDate} onChange={(e)=>updateNotificationFilter("startDate", e.target.value)} style={{minWidth:0,width:"100%",padding:"10px 12px",borderRadius:12,border:`1px solid ${T.border}`,background:T.card,color:T.text,fontSize:13}} />
              <input type="date" value={notificationFilters.endDate} onChange={(e)=>updateNotificationFilter("endDate", e.target.value)} style={{minWidth:0,width:"100%",padding:"10px 12px",borderRadius:12,border:`1px solid ${T.border}`,background:T.card,color:T.text,fontSize:13}} />
            </div>
          ) : null}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <label style={{display:"inline-flex",alignItems:"center",gap:8,fontSize:12,color:T.muted,cursor:"pointer"}}>
              <input type="checkbox" checked={notificationFilters.unreadOnly} onChange={(e)=>updateNotificationFilter("unreadOnly", e.target.checked)} />
              Mostrar só não lidas
            </label>
            <div style={{fontSize:12,color:T.muted}}>{filteredGroupedNotifications.length} assunto(s) encontrado(s)</div>
          </div>
        </div>
        {!filteredGroupedNotifications.length ? (
          <div style={{padding:"18px 14px",borderRadius:14,border:`1px dashed ${T.border}`,background:T.card2,color:T.muted,fontSize:13}}>
            Nenhuma notificação interna encontrada para este usuário nesta fazenda.
          </div>
        ) : filteredGroupedNotifications.map(group=>{
          const latestItem = group.latestItem || {};
          const latestReadMeta = getNotificationReadMeta(latestItem);
          const latestRead = latestReadMeta.isRead;
          const groupHasUnread = Number(group?.unreadCount || 0) > 0;
          const groupExpanded = isNotificationGroupExpanded(group);
          const historyExpanded = !!expandedNotificationHistoryGroups?.[group.groupKey];
          return (
            <div key={group.orderKey} style={{border:`1px solid ${group.unreadCount>0 ? T.accent : T.border}`,background:group.unreadCount>0?`${T.accent}10`:T.card2,borderRadius:14,padding:14,display:"grid",gap:12}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap"}}>
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}><div style={{fontWeight:800,color:T.text,fontSize:14}}>{group.type === "occurrence_event" ? "Ocorrência" : group.type === "service_event" ? "Registro" : group.type === "inspection_event" ? "Vistoria" : group.type === "veterinary_event" ? "Atendimento" : (group.orderLabel ? "Pedido" : (latestItem?.title || "Notificação"))}</div>{group.orderLabel ? <div style={{fontSize:11,fontWeight:800,color:T.accent,letterSpacing:.6,padding:"5px 8px",borderRadius:999,border:`1px solid ${T.accent}33`,background:`${T.accent}12`}}>{group.orderLabel}</div> : null}</div>
                    <Badge color={group.unreadCount>0?T.accent:T.green}>{group.unreadCount>0?`${group.unreadCount} nova(s)`:"Tudo lido"}</Badge>
                    {group.latestStatus ? <Badge color={T.blue}>Status atual: {group.latestStatus}</Badge> : null}
                  </div>
                  <div style={{fontSize:12,color:T.muted,marginTop:4}}>
                    {group.requester ? `Solicitante: ${group.requester}` : ""}
                    {group.requester && group.costCenter ? " · " : ""}
                    {group.costCenter ? `Centro de custo: ${group.costCenter}` : ""}
                  </div>
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
                  <Btn size="sm" variant="ghost" onClick={()=>toggleNotificationGroupExpanded(group)}>{groupExpanded ? "Recolher" : (group.unreadCount>0 ? "Expandir novas" : "Expandir lidas")}</Btn>
                  {group.orderId ? <Btn size="sm" onClick={()=>openInternalNotification(latestItem)}>{group.type === "occurrence_event" ? "Abrir ocorrência" : group.type === "service_event" ? "Abrir serviço" : group.type === "inspection_event" ? "Abrir vistoria" : group.type === "veterinary_event" ? "Abrir atendimento" : "Abrir pedido"}</Btn> : null}
                </div>
              </div>
              {groupExpanded ? (
                <div style={{display:"grid",gap:10}}>
                  <div style={{border:`1px solid ${latestRead ? T.border : `${T.accent}55`}`,background:latestRead?T.card:`${T.accent}0d`,borderRadius:12,padding:12,display:"grid",gap:8}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap"}}>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}><div style={{fontWeight:800,color:T.text,fontSize:13}}>{latestItem?.title || "Notificação"}</div>{getPreferredRecordLabel(latestItem, "") ? <div style={{fontSize:11,fontWeight:800,color:T.accent,letterSpacing:.6,padding:"4px 8px",borderRadius:999,border:`1px solid ${T.accent}33`,background:`${T.accent}10`}}>{getPreferredRecordLabel(latestItem, "")}</div> : null}</div>
                        <div style={{fontSize:12,color:T.muted,marginTop:4}}>{latestItem?.summary || latestItem?.body || "Sem resumo."}</div>
                      </div>
                      <Badge color={latestRead?T.muted:T.accent}>{latestRead?"Lida":"Não lida"}</Badge>
                    </div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",fontSize:11,color:T.muted}}>
                      <span>Módulo: {group.moduleLabel}</span>
                      {latestItem?.eventLabel ? <span>{latestItem.eventLabel}</span> : null}
                      {latestItem?.orderStatus ? <span>Status: {latestItem.orderStatus}</span> : null}
                      <span>{fmtDate(latestItem?.eventAt || latestItem?.createdAt || "")}</span>
                      {latestReadMeta.readAt ? <span>Lida em {fmtDate(latestReadMeta.readAt)}</span> : null}
                    </div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"space-between",alignItems:"center"}}>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        <Btn size="sm" variant="ghost" disabled={!!notificationWriteBusyMap[String(latestItem?.id || latestItem?.clientId || "").trim()]} onClick={()=>{
                          const groupItems = [latestItem, ...(group.olderItems || [])].filter(Boolean).map((item)=> ({ ...item, groupKey: group.groupKey }));
                          syncNotificationsReadState(groupItems, true);
                          if(group.groupKey){
                            setExpandedNotificationGroups((prev)=> ({ ...prev, [group.groupKey]: false }));
                          }
                        }}>{groupHasUnread?"Marcar como lida":"Lida"}</Btn>
                        {(latestItem?.orderId || latestItem?.entityId) ? <Btn size="sm" onClick={()=>openInternalNotification(latestItem)}>{group.openLabel}</Btn> : null}
                      </div>
                      {group.olderItems.length>0 ? <Btn size="sm" variant="ghost" onClick={()=>toggleNotificationHistoryExpanded(group.groupKey)}>{historyExpanded ? `Ocultar histórico (${group.olderItems.length})` : `Ver histórico (${group.olderItems.length})`}</Btn> : null}
                    </div>
                  </div>
                  {historyExpanded && group.olderItems.length>0 ? (
                    <div style={{display:"grid",gap:8,paddingLeft:10,borderLeft:`2px solid ${T.border}`}}>
                      {group.olderItems.map(item=>{
                        const itemId = String(item?.id || "");
                        const readMeta = getNotificationReadMeta(item);
                        const isRead = readMeta.isRead;
                        return (
                          <div key={itemId || Math.random()} style={{border:`1px solid ${isRead ? T.border : `${T.accent}33`}`,background:isRead?T.card:T.card2,borderRadius:12,padding:12,display:"grid",gap:8}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap"}}>
                              <div>
                                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}><div style={{fontWeight:700,color:T.text,fontSize:13}}>{item?.title || "Notificação"}</div>{getPreferredRecordLabel(item, "") ? <div style={{fontSize:11,fontWeight:800,color:T.accent,letterSpacing:.6,padding:"4px 8px",borderRadius:999,border:`1px solid ${T.accent}22`,background:`${T.accent}08`}}>{getPreferredRecordLabel(item, "")}</div> : null}</div>
                                <div style={{fontSize:12,color:T.muted,marginTop:4}}>{item?.summary || item?.body || "Sem resumo."}</div>
                              </div>
                              <Badge color={isRead?T.muted:T.accent}>{isRead?"Lida":"Não lida"}</Badge>
                            </div>
                            <div style={{display:"flex",gap:8,flexWrap:"wrap",fontSize:11,color:T.muted}}>
                              {item?.eventLabel ? <span>{item.eventLabel}</span> : null}
                              {item?.orderStatus ? <span>Status: {item.orderStatus}</span> : null}
                              <span>{fmtDate(item?.eventAt || item?.createdAt || "")}</span>
                              {readMeta.readAt ? <span>Lida em {fmtDate(readMeta.readAt)}</span> : null}
                            </div>
                            <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
                              <Btn size="sm" variant="ghost" disabled={!!notificationWriteBusyMap[String(item?.id || item?.clientId || "").trim()]} onClick={()=>toggleReadNotification({ ...item, groupKey: group.groupKey })}>{isRead?"Lida":"Marcar como lida"}</Btn>
                              {(item?.orderId || item?.entityId) ? <Btn size="sm" onClick={()=>openInternalNotification(item)}>{group.openLabel}</Btn> : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div style={{display:"grid",gap:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap"}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:T.text}}>{latestItem?.title || "Notificação"}</div>
                      <div style={{fontSize:12,color:T.muted,marginTop:4}}>{latestItem?.summary || latestItem?.body || "Sem resumo."}</div>
                    </div>
                    <Badge color={latestRead?T.muted:T.accent}>{latestRead?"Lida":"Não lida"}</Badge>
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",fontSize:11,color:T.muted}}>
                    <span>Módulo: {group.moduleLabel}</span>
                    <span>{fmtDate(latestItem?.eventAt || latestItem?.createdAt || "")}</span>
                    {latestReadMeta.readAt ? <span>Lida em {fmtDate(latestReadMeta.readAt)}</span> : null}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>

    {!module_ && (
      <div style={{position:"fixed",top:"max(72px, calc(env(safe-area-inset-top) + 54px))",left:"max(8px, env(safe-area-inset-left))",bottom:"calc(88px + env(safe-area-inset-bottom))",width:"min(262px, calc(100vw - 16px))",background:T.card,border:`1px solid ${T.border}`,borderRadius:22,padding:"14px 10px",boxShadow:"0 24px 52px rgba(2,6,23,.22)",zIndex:55,transform:farmNavOpen?"translateX(0)":"translateX(calc(-100% - 18px))",opacity:farmNavOpen?1:0,pointerEvents:farmNavOpen?"auto":"none",transition:"transform .22s ease, opacity .18s ease",overflowY:"auto"}}>
        <div style={{fontSize:11,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:.8,padding:"2px 8px 8px"}}>Módulos habilitados</div>
        <div style={{display:"grid",gap:6}}>
          {farmMenuItems.length===0 ? (
            <div style={{padding:"12px 10px",fontSize:13,color:T.muted}}>Nenhum módulo disponível.</div>
          ) : farmMenuItems.map(item=>{
            const active = module_===item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={()=>{ setModuleSessionOverride(null); setModuleBridgeMeta(null); setModule(item.id); setFarmNavOpen(false); }}
                style={{textAlign:"left",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,padding:"10px 11px",borderRadius:14,border:`1px solid ${active?item.color||T.accent:T.border}`,background:active?`${item.color||T.accent}16`:T.card2,color:active?(item.color||T.accent):T.text,cursor:"pointer",fontWeight:700,fontSize:14,boxShadow:active?`0 0 0 1px ${(item.color||T.accent)}22 inset`:"none"}}
              >
                <span style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                  {renderInlineIcon(item.icon, { size: 18, title: item.label })}
                  <span style={{display:"flex",flexDirection:"column",minWidth:0}}>
                    <span style={{fontSize:13,fontWeight:800,color:"currentColor",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.label}</span>
                    <span style={{fontSize:11,fontWeight:500,color:active?(item.color||T.accent):T.muted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.desc}</span>
                  </span>
                </span>
                <span style={{fontSize:12,fontWeight:800,color:active?(item.color||T.accent):T.muted}}>›</span>
              </button>
            );
          })}
        </div>
      </div>
    )}

    <div className="fz-dashboard-pad" style={{padding:"20px 16px",paddingBottom:"calc(112px + env(safe-area-inset-bottom))",maxWidth:1100,margin:"0 auto"}}>
      {!module_&&<>
        <div style={{marginBottom:24}}>
          <div className="fz-dashboard-title" style={{display:"flex",alignItems:"baseline",gap:10,flexWrap:"wrap",fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:700,color:T.text,marginBottom:4}}><span>Olá, {String(session?.name||"Usuário").trim().split(/\s+/)[0]||"Usuário"}!</span><span style={{fontFamily:"'DM Sans',sans-serif",fontSize:11,fontWeight:600,color:T.muted,letterSpacing:"0.08em",textTransform:"uppercase",opacity:0.9}}>v{displayVersion}</span></div>
          <div style={{color:T.muted,fontSize:14,marginBottom:16,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            <span>Módulos de</span>
            <strong style={{color:farm.color,display:"inline-flex",alignItems:"center",gap:6}}>
              <FarmEmoji farm={farm} size={16} />
              <span>{farm.name}</span>
            </strong>
          </div>

          {/* ── Vistoria card (collaborator dashboard) ── */}
          {session?.role==="solicitante"&&(()=>{
            try{
              const farmId=farm?.id; if(!farmId) return null;
              const raw=localStorage.getItem(`fz:${farmId}:harasStalls`);
              if(!raw) return null;
              const stalls=JSON.parse(raw)||[];
              const inspected=stalls.filter(s=>s.lastInspection);
              if(inspected.length===0) return null;
              const latest=[...inspected].sort((a,b)=>new Date(b.lastInspection)-new Date(a.lastInspection))[0];
              const greenCount=stalls.filter(s=>s.lastSeverity==="green").length;
              const orangeCount=stalls.filter(s=>s.lastSeverity==="orange").length;
              const redCount=stalls.filter(s=>s.lastSeverity==="red").length;
              const overallSev=redCount>0?"red":orangeCount>0?"orange":"green";
              const SEV={
                green: {bg:"#052E16",border:"#166534",color:"#22C55E",icon:"✅",title:"Tudo em ordem"},
                orange:{bg:"#1C1207",border:"#92400E",color:"#F97316",icon:"⚠️",title:"Possíveis problemas"},
                red:   {bg:"#3F0A0A",border:"#991B1B",color:"#EF4444",icon:"🚨",title:"Resolução imediata"},
              };
              const s=SEV[overallSev];
              return(
                <div onClick={()=>setModule("haras")}
                  style={{background:s.bg,border:`2px solid ${s.border}`,borderRadius:16,padding:"16px 20px",marginBottom:24,cursor:"pointer",transition:"all .2s"}}
                  onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"}
                  onMouseLeave={e=>e.currentTarget.style.transform="translateY(0)"}>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                    <div style={{width:44,height:44,borderRadius:12,background:`${s.color}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>🏠</div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:800,color:"#F8FAFC",fontSize:15}}>Vistoria das Baias</div>
                      <div style={{fontSize:11,color:"#94A3B8",marginTop:2}}>{new Date(latest.lastInspection).toLocaleString("pt-BR")} · {latest.lastInspector}</div>
                    </div>
                    <span style={{fontSize:22}}>{s.icon}</span>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                    {[
                      {bg:"#052E16",border:"#166534",color:"#22C55E",icon:"✅",label:"Tudo bem",count:greenCount},
                      {bg:"#1C1207",border:"#92400E",color:"#F97316",icon:"⚠️",label:"Possíveis prob.",count:orangeCount},
                      {bg:"#3F0A0A",border:"#991B1B",color:"#EF4444",icon:"🚨",label:"Ação imediata",count:redCount},
                    ].map((item,i)=>(
                      <div key={i} style={{background:item.bg,border:`1px solid ${item.border}`,borderRadius:10,padding:"10px 12px",textAlign:"center"}}>
                        <div style={{fontSize:18,marginBottom:4}}>{item.icon}</div>
                        <div style={{fontSize:22,fontWeight:800,color:item.color,lineHeight:1}}>{item.count}</div>
                        <div style={{fontSize:10,color:item.color,opacity:.85,marginTop:3,fontWeight:600}}>{item.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }catch(e){return null;}
          })()}

          {/* Dashboard cards selected by admin */}
          {(() => {
            const isLightTheme = T.bg === LIGHT_THEME.bg;
            const stockCardPalette = {
              rowBg: isLightTheme ? "#0F3D34" : "#052E2B",
              rowBorder: isLightTheme ? "#166534" : "#0F766E",
              rowTitle: isLightTheme ? "#FFFBEF" : "#F6F1DE",
              rowMeta: isLightTheme ? "#F3EBD1" : "#E8DEC0",
              count: "#6EE7B7",
              countLabel: isLightTheme ? "#D1FAE5" : "#ECFDF5",
              badgeBg: "rgba(110,231,183,.16)",
              badgeText: "#6EE7B7",
              emptyBg: isLightTheme ? "rgba(15,61,52,.08)" : "rgba(15,23,42,.32)",
              emptyText: isLightTheme ? "#355A51" : "#D8CFB3",
              summaryText: isLightTheme ? "#355A51" : "#D8CFB3",
              summaryStrong: isLightTheme ? "#0F172A" : "#FFFBEF",
            };
            return (
          <div className="fz-stat-grid" style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:12,marginBottom:24}}>
            {dashboardCanOpenClimate && (
              <div onClick={()=>{ setModuleSessionOverride(null); setModuleBridgeMeta(null); setModule("climate"); }}
                style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:"18px 20px",cursor:"pointer",transition:"all .2s"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor="#06B6D4";e.currentTarget.style.transform="translateY(-2px)";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.transform="translateY(0)";}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,gap:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    {dashboardClimateCards?.[0]?.visual?.src ? (
                      <div style={{width:42,height:42,borderRadius:12,background:"rgba(6,182,212,.12)",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",padding:4}}>
                        <img src={dashboardClimateCards[0].visual.src} alt={dashboardClimateCards[0].visual.label || "Clima"} style={{width:"100%",height:"100%",objectFit:"contain"}} />
                      </div>
                    ) : (
                      <div style={{width:36,height:36,borderRadius:10,background:"rgba(6,182,212,.16)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>🌦️</div>
                    )}
                    <div>
                      <div style={{fontWeight:700,color:T.text,fontSize:15}}>Clima</div>
                      <div style={{fontSize:11,color:T.muted}}>{dashboardClimateLoading ? "Carregando leitura climática..." : (dashboardClimateSummary?.source_station_name || dashboardClimateSummary?.source_station_id || "Leitura da estação principal")}</div>
                    </div>
                  </div>
                  <div style={{fontSize:13,color:"#06B6D4",fontWeight:600}}>Abrir →</div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"minmax(0,1.08fr) minmax(0,1fr) minmax(0,0.94fr)",gap:10}}>
                  {dashboardClimateCards.map((card)=>{
                    const open = dashboardClimateExpanded === card.id;
                    return (
                      <div key={card.id} onClick={(e)=>{ e.stopPropagation(); setDashboardClimateExpanded(prev=>prev===card.id?"":card.id); }} style={{background: card.id==="current"?"#082F49":(card.id==="max"?"#3B2505":"#062B22"), border:`1px solid ${card.id==="current"?"#0369A1":(card.id==="max"?"#B45309":"#0D9488")}`, borderRadius:12, padding:"12px 14px", textAlign:"left"}}>
                        <div style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr)",alignItems:"center",columnGap:card.id==="current"?4:6}}>
                          {card.visual?.src ? (
                            <img src={card.visual.src} alt={card.visual.label || card.title} style={{width:card.id==="current"?24:28,height:card.id==="current"?24:28,objectFit:"contain",borderRadius:8,justifySelf:"start"}} />
                          ) : (
                            <span style={{fontSize:card.id==="current"?18:20}}>{card.accent}</span>
                          )}
                          <div style={{minWidth:0,overflow:"hidden",display:"flex",alignItems:"baseline"}}>
                            {renderDashboardMetricValue(card.value, card.id==="current"?"#7DD3FC":(card.id==="max"?"#FBBF24":"#5EEAD4"), { compact: card.id==="current" })}
                          </div>
                        </div>
                        <div style={{fontSize:11,fontWeight:600,color:card.id==="current"?"#BAE6FD":(card.id==="max"?"#FDE68A":"#CCFBF1"),letterSpacing:.3,marginTop:4}}>{card.title}</div>
                        {open ? <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${card.id==="current"?"#0369A1":(card.id==="max"?"#B45309":"#0D9488")}`,fontSize:11,color:card.id==="current"?"#E0F2FE":(card.id==="max"?"#FEF3C7":"#D1FAE5"),lineHeight:1.45}}>{card.detail}</div> : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {canSeeCard("services_occurrences") && (
              <div
                style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:"18px 20px",transition:"all .2s"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:36,height:36,borderRadius:10,background:"rgba(59,130,246,.16)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>🔨</div>
                    <div>
                      <div style={{fontWeight:700,color:T.text,fontSize:15}}>Serviços & Ocorrências</div>
                      <div style={{fontSize:11,color:T.muted}}>Resumo operacional da fazenda</div>
                    </div>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,minmax(0,1fr))",gap:10}}>
                  <button
                    onClick={()=>{setServicesTabPreset("internal"); setModule("services");}}
                    style={{background:"#0B2A44",border:"1px solid #1D4ED8",borderRadius:12,padding:"12px 14px",cursor:"pointer",textAlign:"left"}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:20}}>🔨</span>
                      <span style={{fontSize:24,fontWeight:800,color:"#93C5FD",lineHeight:1}}>{svcOpen.length}</span>
                    </div>
                    <div style={{fontSize:11,fontWeight:600,color:"#DBEAFE",letterSpacing:.3,marginTop:4}}>Serviços Abertos</div>
                  </button>
                  <button
                    onClick={()=>setModule("occurrences")}
                    style={{background:"#3F0A0A",border:"1px solid #991B1B",borderRadius:12,padding:"12px 14px",cursor:"pointer",textAlign:"left"}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:20}}>🚨</span>
                      <span style={{fontSize:24,fontWeight:800,color:"#F87171",lineHeight:1}}>{occOpen.length}</span>
                    </div>
                    <div style={{fontSize:11,fontWeight:600,color:"#FECACA",letterSpacing:.3,marginTop:4}}>Ocorrências Abertas</div>
                  </button>
                  <button
                    onClick={()=>{setServicesTabPreset("external"); setModule("services_external");}}
                    style={{background:"#062B22",border:"1px solid #0D9488",borderRadius:12,padding:"12px 14px",cursor:"pointer",textAlign:"left"}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:20}}>👷</span>
                      <span style={{fontSize:24,fontWeight:800,color:"#5EEAD4",lineHeight:1}}>{extOpen.length}</span>
                    </div>
                    <div style={{fontSize:11,fontWeight:600,color:"#CCFBF1",letterSpacing:.3,marginTop:4}}>Serviços Terceirizados Abertos</div>
                  </button>
                </div>
              </div>
            )}

            {canSeeCard("purchases_overview") && allowedMods.some(m=>m.id==="purchases") && (
              <div onClick={()=>{ setModuleSessionOverride(null); setModuleBridgeMeta(null); setModule("purchases"); }}
                style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:"18px 20px",cursor:"pointer",transition:"all .2s"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor="#F59E0B";e.currentTarget.style.transform="translateY(-2px)";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.transform="translateY(0)";}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:36,height:36,borderRadius:10,background:"rgba(245,158,11,.18)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>🛒</div>
                    <div>
                      <div style={{fontWeight:700,color:T.text,fontSize:15}}>Compras</div>
                      <div style={{fontSize:11,color:T.muted}}>{purchaseOpen} pedido{purchaseOpen===1?"":"s"} em aberto</div>
                    </div>
                  </div>
                  <div style={{fontSize:13,color:"#F59E0B",fontWeight:600}}>Abrir →</div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                  <div style={{background:"#3B2505",border:"1px solid #B45309",borderRadius:12,padding:"12px 14px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:20}}>📋</span>
                      <span style={{fontSize:24,fontWeight:800,color:"#FBBF24",lineHeight:1}}>{purchaseOpen}</span>
                    </div>
                    <div style={{fontSize:11,fontWeight:600,color:"#FDE68A",letterSpacing:.3,marginTop:4}}>Pedidos em Aberto</div>
                  </div>
                  <div style={{background:"#2E1065",border:"1px solid #7C3AED",borderRadius:12,padding:"12px 14px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:20}}>⏳</span>
                      <span style={{fontSize:24,fontWeight:800,color:"#C4B5FD",lineHeight:1}}>{purchaseNeedsApproval}</span>
                    </div>
                    <div style={{fontSize:11,fontWeight:600,color:"#DDD6FE",letterSpacing:.3,marginTop:4}}>Faltando Aprovação</div>
                  </div>
                  <div style={{background:"#082F49",border:"1px solid #0369A1",borderRadius:12,padding:"12px 14px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:20}}>🚚</span>
                      <span style={{fontSize:24,fontWeight:800,color:"#7DD3FC",lineHeight:1}}>{purchaseInTransit}</span>
                    </div>
                    <div style={{fontSize:11,fontWeight:600,color:"#BAE6FD",letterSpacing:.3,marginTop:4}}>Em Trânsito</div>
                  </div>
                </div>
              </div>
            )}

            {canSeeCard("stock_alerts") && allowedMods.some(m=>m.id==="stock") && (
              <div onClick={()=>{ setModuleSessionOverride(null); setModuleBridgeMeta(null); setModule("stock"); }}
                style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:"18px 20px",cursor:"pointer",transition:"all .2s"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor="#10B981";e.currentTarget.style.transform="translateY(-2px)";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.transform="translateY(0)";}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:36,height:36,borderRadius:10,background:"rgba(16,185,129,.18)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>📦</div>
                    <div>
                      <div style={{fontWeight:700,color:T.text,fontSize:15}}>Estoque</div>
                      <div style={{fontSize:11,color:T.muted}}>{topLowStockItems.length} item{topLowStockItems.length===1?"":"ns"} críticos entre os mais usados</div>
                    </div>
                  </div>
                  <div style={{fontSize:13,color:"#10B981",fontWeight:600}}>Abrir →</div>
                </div>
                {topLowStockItems.length>0 ? (
                  <div style={{display:"grid",gap:10}}>
                    {topLowStockItems.map((item,idx)=>{
                      const usage=stockUsageMap[String(item.id)]||0;
                      return (
                        <div key={item.id||idx} style={{display:"grid",gridTemplateColumns:"auto 1fr auto",gap:10,alignItems:"center",background:stockCardPalette.rowBg,border:`1px solid ${stockCardPalette.rowBorder}`,borderRadius:12,padding:"10px 12px",boxShadow:isLightTheme?"0 1px 0 rgba(255,255,255,.08) inset":"none"}}>
                          <div style={{width:28,height:28,borderRadius:8,background:stockCardPalette.badgeBg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:stockCardPalette.badgeText,fontWeight:800}}>{idx+1}</div>
                          <div style={{minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:700,color:stockCardPalette.rowTitle,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.name||"Produto sem nome"}</div>
                            <div style={{fontSize:11,color:stockCardPalette.rowMeta}}>Saldo: {Number(item.quantity||0)} {item.unit||""} · Mínimo: {Number(item.minQty||0)} {item.unit||""}</div>
                          </div>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:18,fontWeight:800,color:stockCardPalette.count,lineHeight:1}}>{usage}</div>
                            <div style={{fontSize:10,color:stockCardPalette.countLabel,fontWeight:600}}>uso</div>
                          </div>
                        </div>
                      );
                    })}
                    <div style={{fontSize:11,color:stockCardPalette.summaryText}}>Uso total recente desses itens: <strong style={{color:stockCardPalette.summaryStrong}}>{stockLowUsageTotal}</strong></div>
                  </div>
                ) : (
                  <div style={{background:stockCardPalette.emptyBg,border:`1px solid ${stockCardPalette.rowBorder}`,borderRadius:12,padding:"16px",fontSize:13,color:stockCardPalette.emptyText,textAlign:"center"}}>Nenhum produto crítico entre os mais utilizados no momento.</div>
                )}
              </div>
            )}
          </div>
            );
          })()}
        </div>

        {allowedMods.length===0 && (
          <div style={{color:T.muted,fontSize:14,padding:24,textAlign:"center",background:T.card,border:`1px solid ${T.border}`,borderRadius:16}}>Nenhum módulo disponível. Contacte o administrador.</div>
        )}
      </>}

      {module_ && moduleBridgeMeta?.sourceModule==="sede" && (
        <div style={{marginBottom:12,padding:12,borderRadius:14,border:`1px solid ${T.border}`,background:T.card2,color:T.text,fontSize:13}}>
          🏠 Acesso concedido pelo módulo <strong>Sede</strong> para visualizar e editar registros ligados ao centro de custo <strong>{moduleBridgeMeta?.forcedCostCenter||"Sede"}</strong>, mesmo sem permissão direta no módulo original. A navegação fica focada na Sede durante este acesso.
        </div>
      )}
      {module_ && liveOpenHydration && (
        <div style={{
          margin:"0 0 12px",
          padding:14,
          borderRadius:16,
          border:`1px solid ${liveOpenHydration.status === "done" ? T.green : liveOpenHydration.status === "error" || liveOpenHydration.status === "timeout" ? T.orange : T.border}`,
          background:T.card2,
          boxShadow:liveOpenHydration.status === "loading" ? "0 10px 28px rgba(59,130,246,.16)" : "none",
          color:T.text,
          display:"grid",
          gap:10,
        }}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
            <div style={{minWidth:0}}>
              <div style={{fontSize:14,fontWeight:900,color:T.text}}>Recebendo dados do servidor</div>
              <div style={{fontSize:12,color:T.muted,marginTop:2}}>
                Abrimos o registro com os dados disponíveis da Live e estamos buscando a versão confirmada.
              </div>
            </div>
            <Badge color={liveOpenHydration.status === "done" ? T.green : liveOpenHydration.status === "timeout" || liveOpenHydration.status === "error" ? T.orange : T.accent}>
              {liveOpenHydration.status === "done" ? "Concluído" : liveOpenHydration.status === "timeout" ? "Aguardando" : liveOpenHydration.status === "error" ? "Atenção" : "Carregando"}
            </Badge>
          </div>
          <div style={{height:8,borderRadius:999,background:T.card,border:`1px solid ${T.border}`,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${Math.max(5, Math.min(100, Number(liveOpenHydration.progress || 0)))}%`,background:liveOpenHydration.status === "done" ? T.green : liveOpenHydration.status === "timeout" || liveOpenHydration.status === "error" ? T.orange : T.accent,transition:"width .35s ease"}} />
          </div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
            <div style={{fontSize:12,color:T.muted}}>
              {liveOpenHydration.message || "Sincronizando registro específico..."}
            </div>
            {(liveOpenHydration.status === "timeout" || liveOpenHydration.status === "error") && (
              <Btn size="sm" variant="primary" onClick={retryLiveOpenHydration}>Recarregar dados</Btn>
            )}
          </div>
        </div>
      )}
      <div style={module_ ? {paddingTop:"clamp(6px, 1.2vh, 10px)"} : undefined}>
      {/* v2.224.07: clima sempre disponível; demais módulos bloqueados enquanto gate ativo */}
      {module_==="climate" && <ClimateModule key={`climate:${moduleRenderVersion}`} session={activeSession}/>}
      {!isNativeGateLocked && (
        <>
        {module_==="purchases"   &&<PurchasesHubModule session={activeSession} isGestor={isGestor} openOrderId={purchaseOpenOrder} onClearOpenOrder={()=>setPurchaseOpenOrder(null)} initialTab="orders" showSuppliersTab={adminGlobalsConfig.showSuppliersButtonInModules} purchaseEditWindowConfig={adminGlobalsConfig}/>} 
        {module_==="suppliers"  &&<PurchasesHubModule session={activeSession} isGestor={isGestor} openOrderId={purchaseOpenOrder} onClearOpenOrder={()=>setPurchaseOpenOrder(null)} initialTab="suppliers" showSuppliersTab={adminGlobalsConfig.showSuppliersButtonInModules} purchaseEditWindowConfig={adminGlobalsConfig}/>} 
        {module_==="animals"     &&<AnimalsModule key={`animals:${moduleRenderVersion}`} session={activeSession}/>}
        {module_==="aviculture"  &&<AvicultureModule key={`aviculture:${moduleRenderVersion}`} session={activeSession}/>}
        {module_==="financial"   &&<FinancialModule key={`financial:${moduleRenderVersion}`} session={activeSession} isGestor={isGestor}/>}
        {module_==="hr"          &&<HRModule key={`hr:${moduleRenderVersion}`} isGestor={isGestor} session={activeSession}/>}
        {module_==="my_items"    &&<EquipamentosEPIsModule key={`my_items:${moduleRenderVersion}`} session={activeSession}/>}
        {module_==="vet"         &&<VetModule key={`vet:${moduleRenderVersion}`} session={activeSession} isGestor={isGestor}/>}
        {module_==="stock"       &&<StockModule key={`stock:${moduleRenderVersion}`} session={activeSession} isGestor={isGestor} onNavigate={(mod,params)=>openModule(mod, params||{})} showCatalogTab={adminGlobalsConfig.showCatalogButtonInModules} moduleCardsConfig={adminGlobalsConfig?.moduleCardsConfig || {}}/>} 
        {module_==="machinery"   &&<MachineryModule key={`machinery:${moduleRenderVersion}`} session={activeSession} isGestor={isGestor} moduleCardsConfig={adminGlobalsConfig?.moduleCardsConfig || {}}/>}
        {module_==="haras"       &&<HarasModule key={`haras:${moduleRenderVersion}`} session={activeSession} moduleCardsConfig={adminGlobalsConfig?.moduleCardsConfig || {}}/>}
        {module_==="pastures"    &&<LocationHubModule key={`pastures:${moduleRenderVersion}`} session={activeSession} isGestor={isGestor} poiPreset={poiPreset} onConsumePoiPreset={()=>setPoiPreset(null)}/>}
        {module_==="sede"        &&<SedeModule key={`sede:${moduleRenderVersion}`} session={session} isGestor={isGestor} onNavigate={(mod,params)=>openModule(mod, params||{})}/>}

        {module_==="services"    &&<ServicesModule key={`services:${moduleRenderVersion}`} session={activeSession} isGestor={isGestor} initialTab={servicesTabPreset} mode="internal" showProvidersTab={adminGlobalsConfig.showProvidersButtonInModules} openRecordId={serviceOpenRecord} onClearOpenRecord={handleClearServiceOpenRecord}/>} 
        {module_==="services_external" &&<ServicesModule key={`services_external:${moduleRenderVersion}`} session={activeSession} isGestor={isGestor} initialTab={servicesTabPreset} mode="external" showProvidersTab={adminGlobalsConfig.showProvidersButtonInModules} openRecordId={serviceOpenRecord} onClearOpenRecord={handleClearServiceOpenRecord}/>} 
        {module_==="inspections" &&<InspectionsModule key={`inspections:${moduleRenderVersion}`} session={activeSession} isGestor={isGestor} openInspectionId={inspectionOpenRecord}/>}
        {module_==="occurrences" &&<OccurrencesModule key={`occurrences:${moduleRenderVersion}`} session={activeSession} isGestor={isGestor} openOccurrenceId={occurrenceOpenRecord} onClearOpenOccurrence={handleClearOccurrenceOpenRecord}/>}
        {module_==="poi"         &&<LocationHubModule session={activeSession} isGestor={isGestor} initialTab="poi" poiPreset={poiPreset} onConsumePoiPreset={()=>setPoiPreset(null)}/>}
        {module_==="meu_perfil"  &&<MeuPerfilModule session={activeSession} onSessionUpdate={onSessionUpdate}/>}
        </>
      )}
      </div>
    </div>

    <div style={{
      position:"fixed",
      left:0,
      right:0,
      bottom:0,
      height:nativeFixedTop?"calc(36px + env(safe-area-inset-bottom))":"clamp(72px, 11vh, 96px)",
      zIndex:79,
      pointerEvents:"auto",
      touchAction:"none",
      background:nativeFixedTop ? `linear-gradient(to bottom, color-mix(in srgb, ${T.bg} 22%, rgba(0,0,0,0)) 0%, color-mix(in srgb, ${T.bg} 52%, rgba(0,0,0,0)) 38%, color-mix(in srgb, ${T.bg} 78%, rgba(0,0,0,0)) 72%, color-mix(in srgb, ${T.bg} 92%, rgba(0,0,0,0)) 100%)` : `linear-gradient(to top, color-mix(in srgb, ${T.bg} 96%, rgba(0,0,0,0)) 0%, color-mix(in srgb, ${T.bg} 78%, rgba(0,0,0,0)) 34%, color-mix(in srgb, ${T.bg} 38%, rgba(0,0,0,0)) 68%, rgba(0,0,0,0) 100%)`,
    }} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onPointerDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onTouchStart={(e)=>{ e.preventDefault(); e.stopPropagation(); }} />

    <div style={{
      position:"fixed",
      left:"max(10px, env(safe-area-inset-left))",
      right:"max(10px, env(safe-area-inset-right))",
      bottom:nativeFixedTop?"max(4px, calc(env(safe-area-inset-bottom) - 18px))":"max(0px, calc(env(safe-area-inset-bottom) - 8px))",
      transform:nativeFixedTop?"none":"translateY(6px)",
      zIndex:80,
      pointerEvents:"none",
      display:"flex",
      justifyContent:"center",
    }}>
      <div style={{
        pointerEvents:"auto",
        width:"min(560px, 100%)",
        display:"grid",
        gridTemplateColumns:"repeat(5,minmax(0,1fr))",
        gap:6,
        padding:"6px",
        borderRadius:999,
        border:`1px solid ${shortcutBarTint}2A`,
        background:`color-mix(in srgb, ${shortcutBarTint} ${shortcutBarSurfaceMix}%, rgba(10,14,20,.34))`,
        boxShadow:`0 10px 24px ${shortcutBarTint}1A`,
        backdropFilter:"blur(14px)",
        WebkitBackdropFilter:"blur(14px)",
      }}>
        <button type="button" onClick={()=>{ setModule(null); setModuleSessionOverride(null); setModuleBridgeMeta(null); setPurchaseOpenOrder(null); setServiceOpenRecord(null); setOccurrenceOpenRecord(null); setPoiPreset(null); setServicesTabPreset(null); }} style={{background:`color-mix(in srgb, ${shortcutBarTint} ${shortcutBarButtonMix}%, rgba(8,12,18,.88))`,border:"none",borderRadius:999,minHeight:40,padding:"0",display:"grid",placeItems:"center",cursor:"pointer",opacity:1,boxShadow:`inset 0 0 0 1px ${shortcutBarIconColor}14`}}><ShortcutMonoIcon type="home" size={22} color={shortcutBarIconColor} /></button>
        {quickShortcutModules.map(moduleId=>{
          const active = module_===moduleId;
          const meta = getShortcutChoiceMeta(moduleId);
          return (
            <button key={moduleId} type="button" onClick={()=>{ setModuleSessionOverride(null); setModuleBridgeMeta(null); setModule(moduleId); }} style={{background: active ? `color-mix(in srgb, ${shortcutBarTint} ${shortcutBarButtonMix}%, rgba(8,12,18,.88))` : `color-mix(in srgb, ${shortcutBarTint} ${shortcutBarButtonMix}%, rgba(8,12,18,.88))`,border:"none",borderRadius:999,minHeight:40,padding:"0",display:"grid",placeItems:"center",cursor:"pointer",opacity:1,boxShadow:active?`inset 0 0 0 1px ${shortcutBarIconColor}14, 0 0 0 1px ${shortcutBarTint}1A`: `inset 0 0 0 1px ${shortcutBarIconColor}14`}} title={meta.label} aria-label={meta.label}>
              <ShortcutMonoIcon type={meta.iconType} size={22} color={shortcutBarIconColor} />
            </button>
          );
        })}
      </div>
    </div>

    <style>{`
      @media(max-width:600px){
        html,body{width:100%;overflow-x:hidden;}
        body:not(.fz-native-shell){padding-bottom:env(safe-area-inset-bottom);}
      }
      html.fz-native-shell .fz-dashboard-pad{
        padding-bottom:calc(108px + env(safe-area-inset-bottom)) !important;
      }
    `}</style>
  </div>;
}